import { useState, useEffect, useRef } from "react";
import * as Sentry from '@sentry/react';
import { downloadBackup } from './exportBackup';
import { supabase } from './supabase';
import { curSym, parseIban, validateBic, validateSortCode } from './utils';
import { interpolateTemplate, buildTemplateVars, DEFAULT_TEMPLATES } from './templates.js';
import { resolveTypeLabel } from './room-types.js';
import { CL, FONT, HAIRLINE, st } from './theme.js';
import { ConfirmModal } from './ui.jsx';
import { isSofaBedExtra } from './labels.js';
import { AddRoomModal, CustomTypeForm } from './AddRoomModal.jsx';

const TAVELO_CONTACT_EMAIL = 'hello@tavelo.app';

/* ═══ SETTINGS PANEL ═══ */

const COUNTRY_INFO = {
  GR: { flag: '🇬🇷', name: { en: 'Greece',         bg: 'Гърция'       } },
  BG: { flag: '🇧🇬', name: { en: 'Bulgaria',       bg: 'България'     } },
  GB: { flag: '🇬🇧', name: { en: 'United Kingdom', bg: 'Великобритания' } },
};

function tSettings(lang) {
  const T = {
    en: {
      title: 'Settings',
      sections: {
        hotel:      'Hotel info',
        sources:    'Booking sources',
        extras:     'Extras',
        ota:        'OTA & commissions',
        breakfast:  'Breakfast',
        bookingPage:'Direct booking page',
        bank:       'Bank details',
        deposit:    'Deposit amount',
        templates:  'Guest Communications',
        payments:   'Payments (Stripe)',
        notifications:'Notifications',
        backup:     'Data backup',
      },
      save: 'Save',
      saved: 'Saved',
      unsaved: 'unsaved',
      saveFailed: 'Save failed — try again',
      fixHighlighted: 'Please fix the highlighted fields',
      backConfirmTitle: 'Unsaved changes',
      backConfirmBody: 'You have unsaved changes in some sections. Leave anyway?',
      backConfirmStay: 'Stay',
      backConfirmLeave: 'Leave',
      contactSupport: 'Contact support to change',
      backup: {
        helper: "Download a copy of all your hotel's data — bookings, guests, rooms, and settings. Opens in Excel.",
        button: 'Download backup',
        preparing: 'Preparing…',
        done: '✓ Backup downloaded',
        error: 'Backup failed — please try again',
      },
    },
    bg: {
      title: 'Настройки',
      sections: {
        hotel:      'Данни за хотела',
        sources:    'Източници на резервации',
        extras:     'Екстри',
        ota:        'OTA и комисионни',
        breakfast:  'Закуска',
        bookingPage:'Директна резервация',
        bank:       'Банкови данни',
        deposit:    'Сума на депозита',
        templates:  'Гост-комуникации',
        payments:   'Плащания (Stripe)',
        notifications:'Известия',
        backup:     'Архивиране на данни',
      },
      save: 'Запази',
      saved: 'Запазено',
      unsaved: 'незапазено',
      saveFailed: 'Грешка при запазване',
      fixHighlighted: 'Моля, поправете полетата маркирани в червено',
      backConfirmTitle: 'Незапазени промени',
      backConfirmBody: 'Имате незапазени промени. Да излезете ли?',
      backConfirmStay: 'Остани',
      backConfirmLeave: 'Излез',
      contactSupport: 'Свържете се с поддръжка за промяна',
      backup: {
        helper: 'Изтеглете копие на всички данни на хотела: резервации, гости, стаи и настройки. Отваря се в Excel.',
        button: 'Изтегли архив',
        preparing: 'Подготовка…',
        done: '✓ Архивът е изтеглен',
        error: 'Неуспешно архивиране. Опитайте отново',
      },
    },
  };
  return T[lang] || T.en;
}

function isSectionComplete(key, property) {
  if (!property) return null;
  switch (key) {
    case 'hotel':
      return !!(property.name && property.country && property.address);
    case 'sources':
      return null;
    case 'extras':
      return null;
    case 'ota':
      return property.bcom_commission != null;
    case 'breakfast':
      return property.breakfast_enabled !== null && property.breakfast_enabled !== undefined;
    case 'bookingPage':
      return !!property.slug && (property.has_booking_page !== null && property.has_booking_page !== undefined);
    case 'bank':
      return !!(property.bank_iban && property.bank_iban.trim());
    case 'deposit':
      return !!property.deposit_policy_mode;
    case 'templates':
      return null;
    case 'payments':
      return !!property.stripe_restricted_key;
    case 'notifications':
      // Spec: notify_email set OR explicit null (account email fallback).
      // Anything other than undefined counts as "decided".
      return property.notify_email !== undefined;
    default:
      return null;
  }
}

function SettingsPanel({ property, lang, directUrl, onSave, showToast, onBack, rooms }) {
  const tS = tSettings(lang);
  const SECTION_KEYS = ['hotel','sources','extras','ota','breakfast','bookingPage','bank','templates','deposit','payments','notifications','backup'];
  const [openSections, setOpenSections] = useState({});
  const [dirty, setDirty] = useState({});
  const [backConfirm, setBackConfirm] = useState(false);
  const [slugLocked, setSlugLocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!property?.id) return;
      const { count, error } = await supabase
        .from('booking_requests')
        .select('id', { count: 'exact', head: true })
        .eq('property_id', property.id);
      if (cancelled) return;
      if (!error && (count ?? 0) > 0) setSlugLocked(true);
    })();
    return () => { cancelled = true; };
  }, [property?.id]);

  const toggleOpen = key => setOpenSections(s => ({ ...s, [key]: !s[key] }));
  const markDirty = (key, on = true) => setDirty(d => ({ ...d, [key]: on }));
  const hasDirty = Object.values(dirty).some(Boolean);

  function handleBack() {
    if (hasDirty) { setBackConfirm(true); return; }
    onBack();
  }

  async function saveSection(key, updates) {
    try {
      await onSave(updates);
      markDirty(key, false);
    } catch (e) {
      showToast(tS.saveFailed);
      console.error('Save failed:', e);
    }
  }

  return (
    <div>
      {/* Drill-down header — ‹ back + Cormorant title (matches booking detail / guest profile chrome) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 12px', background: CL.cream, position: 'sticky', top: 0, zIndex: 50, borderBottom: `1px solid ${HAIRLINE}` }}>
        <button onClick={handleBack} aria-label="Back" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 22, lineHeight: 1, color: CL.bark, width: 22 }}>‹</button>
        <span style={{ fontFamily: FONT.display, fontSize: 17, fontWeight: 500, color: CL.bark }}>{tS.title}</span>
        <span style={{ width: 22 }} />
      </div>

      {/* Accordion list */}
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '10px 14px 40px' }}>
        {SECTION_KEYS.map(key => {
          const showBadges = !property?.setup_badges_dismissed;
          // Deposit section only makes sense when guest comms + deposit request are active
          if (key === 'deposit') {
            const commEnabled = property?.guest_comm_enabled !== false;
            const depositEnabled = property?.message_template_enabled?.deposit_request !== false;
            if (!commEnabled || !depositEnabled) return null;
          }
          return (
            <SettingsAccordion
              key={key}
              title={tS.sections[key]}
              open={!!openSections[key]}
              dirty={!!dirty[key]}
              unsavedLabel={tS.unsaved}
              onToggle={() => toggleOpen(key)}
              complete={showBadges ? isSectionComplete(key, property) : null}
            >
              {renderBody(key, { property, lang, tS, directUrl, showToast, saveSection, markDirty, rooms, onSave, slugLocked })}
            </SettingsAccordion>
          );
        })}
        {!property?.setup_badges_dismissed && (
          <div style={{ textAlign: 'center', paddingTop: 4, paddingBottom: 8 }}>
            <button
              type="button"
              onClick={async () => { await onSave({ setup_badges_dismissed: true }); }}
              style={{ background: 'none', border: 'none', color: CL.stone, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit', padding: 0 }}
            >
              {lang === 'bg' ? 'Скрийте индикаторите за настройка' : 'Dismiss setup guide'}
            </button>
          </div>
        )}
      </div>

      {/* Back-nav confirm modal */}
      {backConfirm && (
        <ConfirmModal
          title={tS.backConfirmTitle}
          body={tS.backConfirmBody}
          cancelLabel={tS.backConfirmStay}
          confirmLabel={tS.backConfirmLeave}
          onCancel={() => setBackConfirm(false)}
          onConfirm={() => { setBackConfirm(false); onBack(); }}
        />
      )}
    </div>
  );
}

function SettingsAccordion({ title, open, dirty, unsavedLabel, onToggle, children, complete }) {
  return (
    <div style={{
      background: CL.cream,
      border: `1px solid ${open ? CL.bark : CL.goldL}`,
      borderRadius: 12, marginBottom: 8, overflow: 'hidden',
      transition: 'border-color 120ms ease',
    }}>
      <button onClick={onToggle} style={{
        width: '100%', background: 'none', border: 'none',
        padding: '13px 16px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        color: CL.bark, fontFamily: 'inherit', textAlign: 'left',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ display: 'inline-block', width: 5, height: 5, background: CL.gold, transform: 'rotate(45deg)', flexShrink: 0 }} />
          {complete === true && (
            <span aria-label="complete" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, borderRadius: '50%', background: CL.olive, color: '#fff', fontSize: 9, fontWeight: 700, flexShrink: 0, lineHeight: 1 }}>✓</span>
          )}
          {complete === false && (
            <span aria-label="incomplete" style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', border: `1px solid ${CL.stone}`, background: 'transparent', flexShrink: 0 }} />
          )}
          <span style={{ fontFamily: FONT.display, fontSize: 18, fontWeight: 500, color: CL.bark, lineHeight: 1.15 }}>{title}</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {dirty && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: CL.terra }} />
              <span style={{ color: CL.terra, fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase' }}>{unsavedLabel}</span>
            </span>
          )}
          <span style={{ color: CL.stone, fontSize: 14, lineHeight: 1 }}>{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 16px 14px', borderTop: `1px solid ${CL.goldL}` }}>
          {children}
        </div>
      )}
    </div>
  );
}

// CustomTypeForm and AddRoomModal are now in AddRoomModal.jsx (imported above).

function renderBody(key, ctx) {
  switch (key) {
    case 'hotel':       return <HotelInfoSection {...ctx} />;
    case 'sources':     return <SourcesSection {...ctx} />;
    case 'extras':      return <ExtrasSection {...ctx} />;
    case 'ota':         return <OtaSection {...ctx} />;
    case 'breakfast':   return <BreakfastSection {...ctx} />;
    case 'bookingPage': return <BookingPageSection {...ctx} />;
    case 'bank':        return <BankSection {...ctx} />;
    case 'deposit':     return <DepositPolicySection {...ctx} />;
    case 'templates':   return <GuestCommSection {...ctx} />;
    case 'payments':    return <PaymentsSection {...ctx} />;
    case 'notifications': return <NotificationsSection {...ctx} />;
    case 'backup':        return <DataBackupSection {...ctx} />;
    default: return null;
  }
}

// Placeholder stubs — replaced accordion-by-accordion in Tasks 11–18.
function HotelInfoSection({ property, lang, tS, directUrl, showToast, saveSection, markDirty, rooms, slugLocked }) {
  const [name, setName] = useState(property?.name ?? '');
  const [address, setAddress] = useState(property?.address ?? '');
  const [checkInTime, setCheckInTime] = useState(property?.check_in_time ?? '11:00');
  const [checkOutTime, setCheckOutTime] = useState(property?.check_out_time ?? '15:00');
  const [googleReviewLink, setGoogleReviewLink] = useState(property?.google_review_link ?? '');
  const [googleMapsLink, setGoogleMapsLink] = useState(property?.google_maps_link ?? '');
  const [wazeLink, setWazeLink] = useState(property?.waze_link ?? '');
  const [currency, setCurrency] = useState(property?.currency ?? 'EUR');
  const [primaryLang, setPrimaryLang] = useState((property?.lang ?? 'en').split(',')[0]);
  const [hasSecondary, setHasSecondary] = useState((property?.lang ?? 'en').includes(','));
  const [hasChildPolicy, setHasChildPolicy] = useState(property?.children_max_age != null);
  const [childrenMaxAge, setChildrenMaxAge] = useState(property?.children_max_age ?? 12);
  const [nameErr, setNameErr] = useState(false);
  const [slug, setSlug] = useState(property?.slug ?? '');
  const [slugStatus, setSlugStatus] = useState({ state: 'idle', suggested: null });
  // state: 'idle' | 'checking' | 'available' | 'taken'
  const [country, setCountry] = useState(property?.country ?? 'GR');
  const [confirmCountry, setConfirmCountry] = useState(null);
  // confirmCountry: null | 'GR' | 'BG' | 'GB' — when non-null, modal is open with that pending value

  const isUk = country === 'GB';
  const countryInfo = COUNTRY_INFO[country] || COUNTRY_INFO.GR;
  const L = lang === 'bg' ? {
    name: 'Име на хотела', country: 'Държава',
    address: 'Адрес', slug: 'Адрес на страницата',
    slugLockedHint:    'Заключено. Гости вече са използвали този адрес. Пишете на hello@tavelo.app, за да го промените.',
    slugAvailableHint: '✓ Свободен',
    slugTakenHint:     '✗ Зает, опитайте',
    slugPrefix:        'book.tavelo.app/',
    checkInTime: 'Час за настаняване', checkOutTime: 'Час за напускане',
    googleReviewLink: 'Google Reviews линк', googleMapsLink: 'Google Maps линк', wazeLink: 'Waze линк',
    currency: 'Валута', language: 'Основен език',
    english: 'Английски', bulgarian: 'Български',
    secondaryToggle: 'Добави втори език',
    childPolicy: 'Имаме детска тарифа за закуска',
    childrenMaxAge: 'Максимална възраст за дете',
    childHint: 'Деца на тази възраст и над плащат пълна цена за закуска',
    currencyHint: 'Повечето хотели в UK таксуват в GBP',
    nameReq: 'Името е задължително',
    copied: 'Копирано!',
    optional: 'по избор',
    countryConfirmTitle: 'Промяна на държава?',
    countryConfirmBody:  'Промяната на държавата няма да обнови автоматично валутата ({cur}) или езика. Редактирайте ги отделно, ако е нужно.',
    countryConfirmCancel:'Отказ',
    countryConfirmGo:    'Продължи',
  } : {
    name: 'Hotel name', country: 'Country',
    address: 'Address', slug: 'Page address',
    slugLockedHint:    'Locked — guests have used this URL. Email hello@tavelo.app to change it.',
    slugAvailableHint: '✓ Available',
    slugTakenHint:     '✗ Taken — try',
    slugPrefix:        'book.tavelo.app/',
    checkInTime: 'Check-in time', checkOutTime: 'Check-out time',
    googleReviewLink: 'Google review link', googleMapsLink: 'Google Maps link', wazeLink: 'Waze link',
    currency: 'Currency', language: 'Primary language',
    english: 'English', bulgarian: 'Bulgarian',
    secondaryToggle: 'Also offer a secondary language',
    childPolicy: 'We have a child breakfast rate',
    childrenMaxAge: 'Children max age',
    childHint: 'Children this age and over pay the full adult breakfast rate',
    currencyHint: 'Most UK hotels charge GBP',
    nameReq: 'Name is required',
    copied: 'Copied!',
    optional: 'optional',
    countryConfirmTitle: 'Change country?',
    countryConfirmBody:  "Changing country won't automatically update your currency (currently {cur}) or language. Edit those separately if needed.",
    countryConfirmCancel:'Cancel',
    countryConfirmGo:    'Continue',
  };

  function bump() { markDirty('hotel', true); }

  function handleCopyUrl() {
    if (!directUrl) return;
    navigator.clipboard.writeText(directUrl).then(() => showToast(L.copied));
  }

  function deriveLang() {
    if (!hasSecondary) return primaryLang;
    const other = primaryLang === 'en' ? 'bg' : 'en';
    return primaryLang + ',' + other;
  }

  useEffect(() => {
    if (slugLocked) return;
    const slugTrimmed = (slug ?? '').trim();
    if (!slugTrimmed || slugTrimmed === (property?.slug ?? '')) {
      setSlugStatus({ state: 'idle', suggested: null });
      return;
    }
    let cancelled = false;
    setSlugStatus({ state: 'checking', suggested: null });
    const timer = setTimeout(async () => {
      const { data: available, error } = await supabase
        .rpc('is_slug_available', { p_slug: slugTrimmed, p_exclude_id: property.id });
      if (cancelled) return;
      if (error) { setSlugStatus({ state: 'idle', suggested: null }); return; }
      if (available) {
        setSlugStatus({ state: 'available', suggested: null });
      } else {
        const base = slugTrimmed.replace(/-\d+$/, '');
        let n = 2;
        while (n < 50) {
          const candidate = `${base}-${n}`;
          const { data: candidateAvail } = await supabase
            .rpc('is_slug_available', { p_slug: candidate, p_exclude_id: property.id });
          if (cancelled) return;
          if (candidateAvail) {
            setSlugStatus({ state: 'taken', suggested: candidate });
            return;
          }
          n++;
        }
        setSlugStatus({ state: 'taken', suggested: `${base}-${Date.now()}` });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [slug, slugLocked, property?.id, property?.slug]);

  function onCountryPick(newCode) {
    if (newCode === country) return;
    const original = property?.country ?? 'GR';
    if (newCode === original) {
      // Picking the original — no confirm needed, just revert local state
      setCountry(original);
      markDirty('hotel', false);
      return;
    }
    setConfirmCountry(newCode);
  }

  async function confirmCountryChange() {
    const newCode = confirmCountry;
    setConfirmCountry(null);
    if (!newCode) return;
    setCountry(newCode);
    // Commit only the country change. Other dirty fields stay dirty in this section.
    await saveSection('hotel', { country: newCode });
  }

  function cancelCountryChange() {
    setConfirmCountry(null);
    // Revert local country state so the pills snap back
    setCountry(property?.country ?? 'GR');
  }

  function onSaveClick() {
    if (!name.trim()) { setNameErr(true); showToast(tS.fixHighlighted); return; }
    setNameErr(false);
    saveSection('hotel', {
      name: name.trim(),
      address: address.trim() || null,
      check_in_time: checkInTime.trim() || null,
      check_out_time: checkOutTime.trim() || null,
      google_review_link: googleReviewLink.trim() || null,
      google_maps_link: googleMapsLink.trim() || null,
      waze_link: wazeLink.trim() || null,
      currency: currency,
      lang: isUk ? 'en' : deriveLang(),
      children_max_age: (property?.breakfast_enabled && hasChildPolicy) ? (Number(childrenMaxAge) || 12) : null,
      infant_max_age:   null,
      ...(slugLocked
        ? {}
        : (() => {
            const slugTrimmed = (slug ?? '').trim();
            if (!slugTrimmed || slugTrimmed === (property?.slug ?? '')) return {};
            const finalSlug = slugStatus.state === 'taken' ? slugStatus.suggested : slugTrimmed;
            return { slug: finalSlug };
          })()),
    });
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: CL.stone, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1, display: 'block' };
  const inputStyle = (err) => ({ width: '100%', padding: '10px 12px', border: '1px solid ' + (err ? CL.terra : CL.goldL), borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff', color: CL.bark, fontFamily: 'inherit' });
  const hintStyle = { fontSize: 11, color: CL.stone, marginTop: 4 };

  return (
    <div style={{ paddingTop: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{L.name}</label>
        <input value={name} onChange={e => { setName(e.target.value); bump(); }} style={inputStyle(nameErr)} />
        {nameErr && <div style={{ fontSize: 11, color: CL.terra, marginTop: 4 }}>{L.nameReq}</div>}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{L.country}</label>
        <select
          value={country}
          onChange={e => onCountryPick(e.target.value)}
          style={{ ...inputStyle(false), appearance: 'auto' }}
        >
          <option value="GR">🇬🇷 {lang === 'bg' ? 'Гърция' : 'Greece'}</option>
          <option value="BG">🇧🇬 {lang === 'bg' ? 'България' : 'Bulgaria'}</option>
          <option value="CY">🇨🇾 Cyprus</option>
          <option value="HR">🇭🇷 Croatia</option>
          <option value="ES">🇪🇸 Spain</option>
          <option value="PT">🇵🇹 Portugal</option>
          <option value="IT">🇮🇹 Italy</option>
          <option value="FR">🇫🇷 France</option>
          <option value="DE">🇩🇪 Germany</option>
          <option value="AT">🇦🇹 Austria</option>
          <option value="CH">🇨🇭 Switzerland</option>
          <option value="NL">🇳🇱 Netherlands</option>
          <option value="BE">🇧🇪 Belgium</option>
          <option value="GB">🇬🇧 {lang === 'bg' ? 'Великобритания' : 'United Kingdom'}</option>
          <option value="IE">🇮🇪 Ireland</option>
          <option value="US">🇺🇸 United States</option>
          <option value="AU">🇦🇺 Australia</option>
        </select>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{L.address}</label>
        <input value={address} onChange={e => { setAddress(e.target.value); bump(); }} style={inputStyle(false)}
          placeholder={lang === 'bg' ? 'напр. ул. Морска 12, Созопол, 8130' : 'e.g. 12 High Street, Brighton, BN1 1AA'} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{L.slug}</label>
        {slugLocked ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, padding: '10px 12px', border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 13, background: CL.sand, color: CL.stone, fontFamily: 'SF Mono, Menlo, monospace', wordBreak: 'break-all' }}>
                {L.slugPrefix}<span style={{ color: CL.bark, fontWeight: 700 }}>{property?.slug || ''}</span>
              </div>
              <button type="button" onClick={handleCopyUrl} title="Copy link" style={{ flexShrink: 0, background: CL.sand, border: '1px solid ' + CL.goldL, borderRadius: 8, cursor: 'pointer', padding: '8px 10px', display: 'flex', alignItems: 'center', color: CL.stone }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
            </div>
            <div style={{ fontSize: 11, color: CL.stone, marginTop: 6, lineHeight: 1.4 }}>{L.slugLockedHint}</div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ flex: 1, fontFamily: 'SF Mono, Menlo, monospace', fontSize: 13, color: CL.bark, padding: '7px 10px', background: CL.sand, borderRadius: 8, border: '1px solid ' + CL.goldL, display: 'flex', alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden' }}>
                <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{L.slugPrefix}</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => { setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); bump(); }}
                  style={{ background: 'transparent', border: 'none', fontFamily: 'SF Mono, Menlo, monospace', fontSize: 13, color: CL.bark, padding: 0, flex: 1, minWidth: 0, outline: 'none', fontWeight: 700 }}
                />
              </div>
              <button type="button" onClick={handleCopyUrl} title="Copy link" style={{ flexShrink: 0, background: CL.sand, border: '1px solid ' + CL.goldL, borderRadius: 8, cursor: 'pointer', padding: '8px 10px', display: 'flex', alignItems: 'center', color: CL.stone }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
            </div>
            {slugStatus.state === 'available' && (
              <div style={{ fontSize: 11, color: CL.olive }}>{L.slugAvailableHint}</div>
            )}
            {slugStatus.state === 'taken' && (
              <div style={{ fontSize: 11, color: CL.terra }}>
                {L.slugTakenHint}{' '}
                <button type="button" onClick={() => setSlug(slugStatus.suggested)}
                  style={{ background: 'none', border: 'none', color: CL.gold, cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>
                  {slugStatus.suggested}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>{L.checkInTime}</label>
          <select value={checkInTime} onChange={e => { setCheckInTime(e.target.value); bump(); }} style={inputStyle(false)}>
            <option value="">—</option>
            {Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>{L.checkOutTime}</label>
          <select value={checkOutTime} onChange={e => { setCheckOutTime(e.target.value); bump(); }} style={inputStyle(false)}>
            <option value="">—</option>
            {Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{L.googleReviewLink} <span style={{ fontWeight: 400, color: CL.stone, textTransform: 'none', letterSpacing: 0 }}>({L.optional})</span></label>
        <input value={googleReviewLink} onChange={e => { setGoogleReviewLink(e.target.value); bump(); }} placeholder="https://g.page/r/..." style={inputStyle(false)} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{L.googleMapsLink} <span style={{ fontWeight: 400, color: CL.stone, textTransform: 'none', letterSpacing: 0 }}>({L.optional})</span></label>
        <input value={googleMapsLink} onChange={e => { setGoogleMapsLink(e.target.value); bump(); }} placeholder="https://maps.google.com/..." style={inputStyle(false)} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{L.wazeLink} <span style={{ fontWeight: 400, color: CL.stone, textTransform: 'none', letterSpacing: 0 }}>({L.optional})</span></label>
        <input value={wazeLink} onChange={e => { setWazeLink(e.target.value); bump(); }} placeholder="https://waze.com/..." style={inputStyle(false)} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>{L.currency}</label>
        <select value={currency} onChange={e => { setCurrency(e.target.value); bump(); }} style={inputStyle(false)}>
          <option value="EUR">EUR (€)</option>
          <option value="GBP">GBP (£)</option>
        </select>
        {!isUk && <div style={hintStyle}>{L.currencyHint}</div>}
      </div>

      {!isUk && (
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>{L.language}</label>
          <div style={{ display: 'flex', gap: 16, marginBottom: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
              <input type="radio" name="primaryLang" value="en" checked={primaryLang === 'en'} onChange={() => { setPrimaryLang('en'); bump(); }} />
              {L.english}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
              <input type="radio" name="primaryLang" value="bg" checked={primaryLang === 'bg'} onChange={() => { setPrimaryLang('bg'); bump(); }} />
              {L.bulgarian}
            </label>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: CL.stone }}>
            <input type="checkbox" checked={hasSecondary} onChange={e => { setHasSecondary(e.target.checked); bump(); }} />
            {L.secondaryToggle}
          </label>
        </div>
      )}

      {property?.breakfast_enabled && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: CL.bark, cursor: 'pointer', marginBottom: hasChildPolicy ? 10 : 0 }}>
            <input type="checkbox" checked={hasChildPolicy}
              onChange={e => { setHasChildPolicy(e.target.checked); bump(); }} />
            {L.childPolicy}
          </label>
          {hasChildPolicy && (
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>{L.childrenMaxAge}</label>
              <input type="number" min="1" max="18" value={childrenMaxAge}
                onChange={e => { setChildrenMaxAge(e.target.value); bump(); }}
                style={{ ...inputStyle(false), width: 100 }} />
              <div style={hintStyle}>{L.childHint}</div>
            </div>
          )}
        </div>
      )}

      <button onClick={onSaveClick}
        style={{ width: '100%', padding: 12, background: CL.bark, color: CL.gold, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        {tS.save}
      </button>
      {confirmCountry && (
        <ConfirmModal
          title={L.countryConfirmTitle}
          body={L.countryConfirmBody.replace('{cur}', property?.currency || 'EUR')}
          cancelLabel={L.countryConfirmCancel}
          confirmLabel={L.countryConfirmGo}
          onCancel={cancelCountryChange}
          onConfirm={confirmCountryChange}
        />
      )}
    </div>
  );
}
function SourcesSection({ property, lang, tS, showToast, saveSection, markDirty }) {
  const [rows, setRows] = useState([...(property?.sources ?? [])]);
  const L = lang === 'bg'
    ? { desc: 'Изберете от кой канал идват резервациите. Операторът избира от този списък, когато въвежда резервация.', add: '+ Добави източник' }
    : { desc: 'Channels through which guests book. The operator picks from this list when entering a booking.', add: '+ Add source' };

  function bump() { markDirty('sources', true); }

  function updateRow(i, v) { const next = [...rows]; next[i] = v; setRows(next); bump(); }
  function removeRow(i)    { setRows(rows.filter((_, j) => j !== i)); bump(); }
  function addRow()        { setRows([...rows, '']); bump(); }

  function onSaveClick() {
    const cleaned = rows.map(s => s.trim()).filter(Boolean);
    saveSection('sources', { sources: cleaned });
    setRows(cleaned);
  }

  return (
    <div style={{ paddingTop: 12 }}>
      <p style={{ fontSize: 12, color: CL.stone, marginBottom: 12 }}>{L.desc}</p>

      {rows.map((src, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input value={src} onChange={e => updateRow(i, e.target.value)}
            style={{ flex: 1, padding: '10px 12px', border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
          <button onClick={() => removeRow(i)}
            style={{ background: 'none', border: 'none', color: CL.terra, cursor: 'pointer', fontSize: 18, padding: '0 6px' }}>×</button>
        </div>
      ))}

      <button onClick={addRow}
        style={{ background: 'none', border: '1px solid ' + CL.goldL, color: CL.bark, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4, marginBottom: 14 }}>
        {L.add}
      </button>

      <button onClick={onSaveClick}
        style={{ width: '100%', padding: 12, background: CL.bark, color: CL.gold, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        {tS.save}
      </button>
    </div>
  );
}
function ExtrasSection({ property, lang, tS, showToast, saveSection, markDirty, rooms }) {
  const langSetting = property?.lang ?? 'en';
  const bilingual = langSetting.includes(',');
  const primary = langSetting.split(',')[0];
  const other = primary === 'en' ? 'bg' : 'en';
  const isUk = (property?.country ?? 'GR') === 'GB';
  const showBilingual = bilingual && !isUk;
  const cur = property?.currency === 'GBP' ? '£' : '€';
  const roomTypes = rooms ? [...new Set(rooms.map(r => r.type).filter(Boolean))] : [];

  const [rows, setRows] = useState(() => {
    const ebPrices = property?.extra_bed_prices ?? {};
    return (property?.extras ?? []).map(x => {
      const pricing_type = x.pricing_type ?? (x.chargeable ? 'per_night' : 'free');
      let per_room_type = x.per_room_type ?? false;
      let prices_by_room = x.prices_by_room ?? {};
      let max_by_room = x.max_by_room ?? {};
      // One-time migration: pull property-level extra_bed_prices into the sofa-bed extra
      if (!x.per_room_type && isSofaBedExtra(x) && Object.keys(ebPrices).length > 0) {
        per_room_type = true;
        prices_by_room = { ...ebPrices };
        max_by_room = { ...(property?.max_extra_beds ?? {}) };
      }
      return {
        en: x.en ?? '', bg: x.bg ?? '',
        pricing_type,
        price: x.price ?? x.price_per_night ?? 0,
        per_room_type,
        prices_by_room,
        max_by_room,
      };
    });
  });

  const L = lang === 'bg' ? {
    desc: 'Екстри, които гостите заявяват при резервиране: кошара, диван, специален повод, трансфер и т.н.',
    add: '+ Добави екстра', hEn: 'Английски', hBg: 'Български',
    free: 'Безплатна', perNight: 'На нощ', perStay: 'За целия престой',
    variesByRoom: 'Различна цена по тип стая',
    roomTypeCol: 'Тип стая', priceCol: 'Цена', maxCol: 'Макс.',
    maxUnit: 'макс.',
  } : {
    desc: 'Add-ons guests can request at booking: baby cot, sofa bed, special occasion, transfer, etc.',
    add: '+ Add extra', hEn: 'English', hBg: 'Bulgarian',
    free: 'Free', perNight: 'Per night', perStay: 'Per stay',
    variesByRoom: 'Price varies by room type',
    roomTypeCol: 'Room type', priceCol: 'Price', maxCol: 'Max',
    maxUnit: 'max',
  };

  function bump() { markDirty('extras', true); }

  function updateRow(i, key, v) {
    const next = [...rows];
    next[i] = { ...next[i], [key]: v };
    setRows(next);
    bump();
  }
  function setPricingType(i, pt) {
    const next = [...rows];
    // Switching away from per_night clears the per_room_type flag
    next[i] = { ...next[i], pricing_type: pt, ...(pt !== 'per_night' ? { per_room_type: false } : {}) };
    setRows(next);
    bump();
  }
  function removeRow(i) { setRows(rows.filter((_, j) => j !== i)); bump(); }
  function addRow() {
    setRows([...rows, { en: '', bg: '', pricing_type: 'free', price: 0, per_room_type: false, prices_by_room: {}, max_by_room: {} }]);
    bump();
  }

  function onSaveClick() {
    const cleaned = rows
      .map(r => {
        const o = {};
        if (r.en.trim()) o.en = r.en.trim();
        if (r.bg.trim()) o.bg = r.bg.trim();
        o.chargeable = r.pricing_type !== 'free';
        o.pricing_type = r.pricing_type;
        if (r.pricing_type !== 'free') {
          if (r.pricing_type === 'per_night' && r.per_room_type) {
            o.per_room_type = true;
            o.prices_by_room = r.prices_by_room;
            o.max_by_room = r.max_by_room;
          } else {
            o.per_room_type = false;
            o.price = Number(r.price) || 0;
          }
        }
        return o;
      })
      .filter(o => o.en || o.bg);

    // Sync property-level extra_bed_prices/max_extra_beds from the first per-room-type
    // per-night extra, so the booking calculation continues to work unchanged
    const prt = cleaned.find(o => o.chargeable && o.per_room_type && o.pricing_type === 'per_night');
    saveSection('extras', {
      extras: cleaned,
      extra_bed_prices: prt?.prices_by_room ?? {},
      max_extra_beds:   prt?.max_by_room   ?? {},
    });
    setRows(cleaned.map(o => ({
      en: o.en ?? '', bg: o.bg ?? '',
      pricing_type: o.pricing_type,
      price: o.price ?? 0,
      per_room_type: o.per_room_type ?? false,
      prices_by_room: o.prices_by_room ?? {},
      max_by_room: o.max_by_room ?? {},
    })));
  }

  const inp = { padding: '8px 10px', border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', minWidth: 0, background: '#fff', color: CL.bark, fontFamily: 'inherit' };

  return (
    <div style={{ paddingTop: 12 }}>
      <p style={{ fontSize: 12, color: CL.stone, marginBottom: 12 }}>{L.desc}</p>

      {showBilingual && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 32px', gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 700, color: CL.stone, textTransform: 'uppercase' }}>
          <div>{primary === 'en' ? L.hEn : L.hBg}</div>
          <div>{other === 'en' ? L.hEn : L.hBg}</div>
          <div />
        </div>
      )}

      {rows.map((r, i) => (
        <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${CL.goldL}` }}>
          {/* Name row */}
          <div style={{ display: 'grid', gridTemplateColumns: showBilingual ? '1fr 1fr 32px' : '1fr 32px', gap: 6, marginBottom: 8 }}>
            <input value={r[primary]} onChange={e => updateRow(i, primary, e.target.value)}
              placeholder={lang === 'bg' ? 'напр. Допълнително легло' : 'e.g. Extra bed'} style={inp} />
            {showBilingual && (
              <input value={r[other]} onChange={e => updateRow(i, other, e.target.value)} style={inp} />
            )}
            <button onClick={() => removeRow(i)}
              style={{ background: 'none', border: 'none', color: CL.terra, cursor: 'pointer', fontSize: 18, padding: '0 4px', alignSelf: 'center' }}>×</button>
          </div>

          {/* Pricing type: Free / Per night / Per stay */}
          <div style={{ display: 'flex', gap: 14, paddingLeft: 2, marginBottom: 8, flexWrap: 'wrap' }}>
            {['free', 'per_night', 'per_stay'].map(pt => (
              <label key={pt} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: CL.bark, cursor: 'pointer' }}>
                <input type="radio" name={`pt-${i}`} checked={r.pricing_type === pt}
                  onChange={() => setPricingType(i, pt)} style={{ accentColor: CL.olive }} />
                {L[pt === 'free' ? 'free' : pt === 'per_night' ? 'perNight' : 'perStay']}
              </label>
            ))}
          </div>

          {/* Price controls — only when chargeable */}
          {r.pricing_type !== 'free' && (
            <div style={{ paddingLeft: 2 }}>
              {/* "Varies by room type" checkbox — only for per_night with room types defined */}
              {r.pricing_type === 'per_night' && roomTypes.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: CL.bark, cursor: 'pointer', marginBottom: 8 }}>
                  <input type="checkbox" checked={!!r.per_room_type}
                    onChange={e => updateRow(i, 'per_room_type', e.target.checked)}
                    style={{ accentColor: CL.olive }} />
                  {L.variesByRoom}
                </label>
              )}

              {r.per_room_type && r.pricing_type === 'per_night' ? (
                // Per-room-type price + max table
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, marginBottom: 4, fontSize: 10, fontWeight: 700, color: CL.stone, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <span>{L.roomTypeCol}</span>
                    <span>{L.priceCol}</span>
                    <span>{L.maxCol}</span>
                  </div>
                  {roomTypes.map(type => (
                    <div key={type} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: CL.bark }}>
                        {resolveTypeLabel(type, property?.room_type_info, lang)}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 12, color: CL.stone }}>{cur}</span>
                        <input type="number" min="0" step="0.5"
                          value={r.prices_by_room[type] ?? 0}
                          onChange={e => { updateRow(i, 'prices_by_room', { ...r.prices_by_room, [type]: parseFloat(e.target.value) || 0 }); }}
                          style={{ ...inp, width: 60, textAlign: 'right', padding: '6px 8px' }} />
                        <span style={{ fontSize: 12, color: CL.stone }}>/night</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="number" min="0"
                          value={r.max_by_room[type] ?? 0}
                          onChange={e => { updateRow(i, 'max_by_room', { ...r.max_by_room, [type]: parseInt(e.target.value) || 0 }); }}
                          style={{ ...inp, width: 50, textAlign: 'right', padding: '6px 8px' }} />
                        <span style={{ fontSize: 12, color: CL.stone }}>{L.maxUnit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Flat price
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, color: CL.stone }}>{cur}</span>
                  <input type="number" min="0" step="0.5" value={r.price}
                    onChange={e => updateRow(i, 'price', e.target.value)}
                    style={{ ...inp, width: 80 }} />
                  <span style={{ fontSize: 12, color: CL.stone }}>
                    {r.pricing_type === 'per_night' ? '/night' : '/stay'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <button onClick={addRow}
        style={{ background: 'none', border: '1px solid ' + CL.goldL, color: CL.bark, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4, marginBottom: 16 }}>
        {L.add}
      </button>

      <button onClick={onSaveClick}
        style={{ width: '100%', padding: 12, background: CL.bark, color: CL.gold, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        {tS.save}
      </button>
    </div>
  );
}
function OtaSection({ property, lang, tS, showToast, saveSection, markDirty }) {
  const [trackOta, setTrackOta] = useState(property?.track_ota_commission ?? false);
  const [hasBcom, setHasBcom] = useState(property?.has_bcom ?? false);
  const [bcomComm, setBcomComm] = useState(property?.bcom_commission ?? 15);
  const [bcomVccFee, setBcomVccFee] = useState(property?.bcom_vcc_fee_pct ?? 1.5);
  const [bcomPaidViaOta, setBcomPaidViaOta] = useState(property?.bcom_paid_via_ota ?? true);
  const [cityTaxRate, setCityTaxRate] = useState(property?.city_tax_rate ?? 0);

  const otaConfig = property?.ota_config ?? [];
  const airbnbEntry = otaConfig.find(e => e.name === 'Airbnb');
  const expediaEntry = otaConfig.find(e => e.name === 'Expedia / Hotels.com');
  const [hasAirbnb, setHasAirbnb] = useState(!!airbnbEntry);
  const [airbnbComm, setAirbnbComm] = useState(airbnbEntry?.commission_pct ?? 14);
  const [hasExpedia, setHasExpedia] = useState(!!expediaEntry);
  const [expediaComm, setExpediaComm] = useState(expediaEntry?.commission_pct ?? 20);

  const L = lang === 'bg'
    ? {
        track: 'Следи комисионната на OTA', trackDesc: 'Показва разбивка на комисионни и нетни приходи',
        commission:"Комисиона", vccFee:"VCC/банкова такса",
        commissionHint:"Въведете общата ви ставка. Ако сте Preferred Partner, Booking.com вече е увеличил процента ви до 18–22%. Въведете пълния процент тук. Намерете в Extranet → Finance → Commission или Partner Hub → Programme details.",
        vccFeeHint:"Намерете в Booking.com Extranet → Finance → Virtual credit card. Обикновено 1.1–3.1% в зависимост от валутата и страната. Задайте 0 ако всичките ви резервации са pay-at-property.",
        paidViaOta: 'Гостите плащат през Booking.com',
        paidViaOtaHint: 'При включено: новите резервации от Booking.com са автоматично платени, без депозит. Изключете, ако гостите плащат директно при пристигане.',
        cityTaxLabelGR:"Туристически данък на стая на нощ (€)",
        cityTaxLabelBG:"Туристически данък на човек на нощ (лв.)",
        cityTaxHintGR:"Варира по звездност (€0.50–€4). Проверете ставката в общинския сайт или AADE.",
        cityTaxHintBG:"Определя се от общината.",
      }
    : {
        track: 'Track OTA commission', trackDesc: 'Shows commission breakdown and net revenue on OTA bookings',
        commission:"Commission", vccFee:"VCC/payment fee",
        commissionHint:"Enter your total combined rate. If you are a Preferred Partner, Booking.com has already increased your rate to 18–22% — enter the full number here. Do not enter just the partner premium. Found in Extranet → Finance → Commission, or Partner Hub → Programme details.",
        vccFeeHint:"Found in Booking.com Extranet → Finance → Virtual credit card. Typically 1.1–3.1% depending on currency and country. Set to 0 if all your Bcom bookings are pay-at-property.",
        paidViaOta: 'Guests pay through Booking.com',
        paidViaOtaHint: 'When on, new Booking.com bookings start as Paid with no deposit. Turn off if guests pay you directly on arrival.',
        cityTaxLabelGR:"City/tourist tax per room per night (€)",
        cityTaxLabelBG:"Туристически данък на човек на нощ (лв.)",
        cityTaxHintGR:"Varies by star rating (€0.50–€4). Find your rate via your local municipality or AADE.",
        cityTaxHintBG:"Determined by your municipality.",
      };

  function bump() { markDirty('ota', true); }

  function onSaveClick() {
    const config = [];
    if (hasBcom) {
      const entry = { name: 'Booking.com', commission_pct: Number(bcomComm) };
      config.push(entry);
    }
    if (hasAirbnb)  config.push({ name: 'Airbnb',               commission_pct: Number(airbnbComm) });
    if (hasExpedia) config.push({ name: 'Expedia / Hotels.com', commission_pct: Number(expediaComm) });

    saveSection('ota', {
      track_ota_commission: trackOta,
      has_bcom: hasBcom,
      bcom_commission: Number(bcomComm),
      bcom_vcc_fee_pct: Number(bcomVccFee),
      bcom_paid_via_ota: bcomPaidViaOta,
      city_tax_rate: Number(cityTaxRate),
      ota_config: config,
    });
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: CL.stone, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1, display: 'block' };
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff', color: CL.bark, fontFamily: 'inherit' };

  function Toggle({ on, onChange, label }) {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
        <span style={{ position: 'relative', width: 40, height: 22, flexShrink: 0, background: on ? CL.olive : CL.goldL, borderRadius: 22, transition: 'background 0.2s' }}>
          <span style={{ position: 'absolute', width: 16, height: 16, left: on ? 22 : 3, top: 3, background: '#fff', borderRadius: '50%', transition: 'left 0.2s' }} />
          <input type="checkbox" checked={on} onChange={e => onChange(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, color: CL.bark }}>{label}</span>
      </label>
    );
  }

  return (
    <div style={{ paddingTop: 12 }}>
      <Toggle on={trackOta} onChange={v => { setTrackOta(v); bump(); }} label={L.track} />
      <p style={{ fontSize: 11, color: CL.stone, marginTop: -6, marginBottom: 16 }}>{L.trackDesc}</p>

      {trackOta && (
        <>
          {/* Booking.com */}
          <div style={{ background: CL.sand, borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <Toggle on={hasBcom} onChange={v => { setHasBcom(v); bump(); }} label="Booking.com" />
            {hasBcom && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelStyle}>{L.commission}</label>
                  <input type="number" min="0" value={bcomComm} onChange={e => { setBcomComm(e.target.value); bump(); }} style={inputStyle} />
                  <div style={{ fontSize: 11, color: CL.stone, marginTop: 4, lineHeight: 1.4 }}>{L.commissionHint}</div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label style={labelStyle}>{L.vccFee} %</label>
                  <input type="number" min="0" step="0.1" value={bcomVccFee} onChange={e => { setBcomVccFee(e.target.value); bump(); }} style={inputStyle} />
                  <div style={{ fontSize: 11, color: CL.stone, marginTop: 4, lineHeight: 1.4 }}>{L.vccFeeHint}</div>
                </div>
                {property?.country !== 'GB' && (
                  <div style={{ marginTop: 8 }}>
                    <label style={labelStyle}>
                      {property?.country === 'BG' ? L.cityTaxLabelBG : L.cityTaxLabelGR}
                    </label>
                    <input type="number" min="0" step="0.01" value={cityTaxRate} onChange={e => { setCityTaxRate(e.target.value); bump(); }} style={inputStyle} />
                    <div style={{ fontSize: 11, color: CL.stone, marginTop: 4, lineHeight: 1.4 }}>
                      {property?.country === 'BG' ? L.cityTaxHintBG : L.cityTaxHintGR}
                    </div>
                  </div>
                )}
                {/* Auto-Paid behaviour — when guests pay through Booking.com directly, new
                    Bcom-source bookings start as Paid + no deposit. Off if the hotel collects
                    payment on arrival from Bcom guests. */}
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${CL.goldL}` }}>
                  <Toggle on={bcomPaidViaOta} onChange={v => { setBcomPaidViaOta(v); bump(); }} label={L.paidViaOta} />
                  <div style={{ fontSize: 11, color: CL.stone, marginTop: -4, lineHeight: 1.4 }}>{L.paidViaOtaHint}</div>
                </div>
              </>
            )}
          </div>

          {/* Airbnb */}
          <div style={{ background: CL.sand, borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <Toggle on={hasAirbnb} onChange={v => { setHasAirbnb(v); bump(); }} label="Airbnb" />
            {hasAirbnb && (
              <div>
                <label style={labelStyle}>{L.commission}</label>
                <input type="number" min="0" value={airbnbComm} onChange={e => { setAirbnbComm(e.target.value); bump(); }} style={inputStyle} />
              </div>
            )}
          </div>

          {/* Expedia */}
          <div style={{ background: CL.sand, borderRadius: 8, padding: 12, marginBottom: 14 }}>
            <Toggle on={hasExpedia} onChange={v => { setHasExpedia(v); bump(); }} label="Expedia / Hotels.com" />
            {hasExpedia && (
              <div>
                <label style={labelStyle}>{L.commission}</label>
                <input type="number" min="0" value={expediaComm} onChange={e => { setExpediaComm(e.target.value); bump(); }} style={inputStyle} />
              </div>
            )}
          </div>
        </>
      )}

      <button onClick={onSaveClick}
        style={{ width: '100%', padding: 12, background: CL.bark, color: CL.gold, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        {tS.save}
      </button>
    </div>
  );
}
function BreakfastSection({ property, lang, tS, showToast, saveSection, markDirty }) {
  // Offering state derived from breakfast_enabled + breakfast_pricing_mode:
  //   'none'     → enabled=false
  //   'included' → enabled=true,  mode='included'
  //   'charged'  → enabled=true,  mode='per_person' | 'per_room'
  const initialOffering = !property?.breakfast_enabled
    ? 'none'
    : property?.breakfast_pricing_mode === 'included'
      ? 'included'
      : 'charged';
  const [offering, setOffering] = useState(initialOffering);
  const [chargedMode, setChargedMode] = useState(
    property?.breakfast_pricing_mode === 'included' || !property?.breakfast_pricing_mode
      ? 'per_person'
      : property.breakfast_pricing_mode
  );
  const [price, setPrice] = useState(property?.breakfast_price_per_person ?? 0);
  const cur = curSym(property?.currency);

  const L = lang === 'bg'
    ? {
        offering: 'Закуска',
        none: 'Не се предлага',
        included: 'Включена в цената',
        charged: 'Допълнително заплащане',
        mode: 'Ценообразуване',
        perPerson: 'На човек',
        perRoom: 'На стая',
        price: 'Цена',
        note: 'Избор за закуска от госта в процеса на резервация ще бъде добавен по-късно.',
      }
    : {
        offering: 'Breakfast',
        none: 'Not offered',
        included: 'Included in room price',
        charged: 'Charged separately',
        mode: 'Pricing mode',
        perPerson: 'Per person',
        perRoom: 'Per room',
        price: 'Price',
        note: 'Guest-facing breakfast selection inside the booking flow is scheduled as a follow-up.',
      };

  function bump() { markDirty('breakfast', true); }

  function onSaveClick() {
    if (offering === 'none') {
      saveSection('breakfast', {
        breakfast_enabled: false,
        breakfast_pricing_mode: property?.breakfast_pricing_mode ?? 'per_person',
        breakfast_price_per_person: 0,
      });
    } else if (offering === 'included') {
      saveSection('breakfast', {
        breakfast_enabled: true,
        breakfast_pricing_mode: 'included',
        breakfast_price_per_person: 0,
      });
    } else {
      saveSection('breakfast', {
        breakfast_enabled: true,
        breakfast_pricing_mode: chargedMode,
        breakfast_price_per_person: Number(price) || 0,
      });
    }
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: CL.stone, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1, display: 'block' };
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff', color: CL.bark, fontFamily: 'inherit' };

  return (
    <div style={{ paddingTop: 12 }}>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{L.offering}</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { val: 'none',     lbl: L.none },
            { val: 'included', lbl: L.included },
            { val: 'charged',  lbl: L.charged },
          ].map(opt => (
            <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: CL.bark }}>
              <input type="radio" name="breakfastOffering" value={opt.val} checked={offering === opt.val}
                onChange={() => { setOffering(opt.val); bump(); }} />
              {opt.lbl}
            </label>
          ))}
        </div>
      </div>

      {offering === 'charged' && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>{L.mode}</label>
            <div style={{ display: 'flex', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: CL.bark }}>
                <input type="radio" name="breakfastMode" value="per_person" checked={chargedMode === 'per_person'} onChange={() => { setChargedMode('per_person'); bump(); }} />
                {L.perPerson}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: CL.bark }}>
                <input type="radio" name="breakfastMode" value="per_room" checked={chargedMode === 'per_room'} onChange={() => { setChargedMode('per_room'); bump(); }} />
                {L.perRoom}
              </label>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>{L.price} ({cur})</label>
            <input type="number" min="0" value={price} onChange={e => { setPrice(e.target.value); bump(); }} style={inputStyle} />
          </div>

          <p style={{ fontSize: 11, color: CL.stone, marginBottom: 14, fontStyle: 'italic' }}>{L.note}</p>
        </>
      )}

      <button onClick={onSaveClick}
        style={{ width: '100%', padding: 12, background: CL.bark, color: CL.gold, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        {tS.save}
      </button>
    </div>
  );
}
function BookingPageSection({ property, lang, tS, directUrl, showToast, saveSection, markDirty, onSave }) {
  const [enabled, setEnabled] = useState(property?.has_booking_page ?? true);
  const [contacts, setContacts] = useState(property?.booking_page_contacts ?? ['phone','whatsapp','viber','email']);
  const [tagline, setTagline] = useState(property?.public_tagline || '');
  const [hotelPhone, setHotelPhone] = useState(property?.phone || '');
  const [mapsLink, setMapsLink] = useState(property?.google_maps_link || '');
  const [reviewsCount, setReviewsCount] = useState(property?.google_reviews_count != null ? String(property.google_reviews_count) : '');
  const [reviewsRating, setReviewsRating] = useState(property?.google_reviews_rating != null ? String(property.google_reviews_rating) : '');
  const [currency, setCurrency] = useState(property?.currency || 'EUR');
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmRevertHero, setConfirmRevertHero] = useState(false);
  const [revertingHero, setRevertingHero] = useState(false);

  const L = lang === 'bg'
    ? {
        enable: 'Активирай публичната страница за резервация',
        contacts: 'Методи за контакт', copy: 'Копирай', copied: '✓ Копирано!', qr: 'Свали QR код',
        confirmTitle: 'Изключване на публичната страница',
        confirmBody: 'Изключването на публичната страница ще направи вашия /book/ линк неработещ веднага. QR кодове и линкове, които сте споделили, ще дават грешка. Продължавате ли?',
        confirmStay: 'Отказ', confirmGo: 'Продължи',
        appearance: 'Външен вид на страницата',
        taglineLbl: 'Подзаглавие', taglinePh: 'напр. Тих 18-стаен хотел над Егейско море',
        phoneLbl: 'Телефон на хотела', phonePh: '+30 25910 12345',
        phoneHelp: 'Показва се на екрана за потвърждение, за да могат гостите да се свържат бързо.',
        mapsLbl: 'Google Maps линк', mapsPh: 'https://maps.app.goo.gl/…',
        reviewsLbl: 'Google Reviews', reviewsCount: 'Брой отзиви', reviewsRating: 'Оценка (0–5)',
        currencyLbl: 'Валута', currencyPh: 'EUR',
        heroEyebrow: 'Снимка за hero',
        heroState1Body: 'Искате собствена снимка на страницата си? Изпратете 2-3 висококачествени снимки на {email} с името на хотела в темата. Ще прегледаме и приложим най-добрата в рамките на 2-3 работни дни.',
        heroState1Status: 'В момента вашата страница използва стандартния Tavelo hero.',
        heroState2BodyChange: 'Искате да я промените? Изпратете нови снимки на {email}.',
        heroRevertLink: 'Вместо това използвайте стандартния Tavelo hero',
        heroConfirmTitle: 'Връщане към стандартния hero',
        heroConfirmBody: 'Това ще премахне снимката от вашата страница за резервация. Гостите ще видят стандартния Tavelo hero. Продължавате ли?',
        heroConfirmCancel: 'Отказ',
        heroConfirmGo: 'Върни към стандартния',
      }
    : {
        enable: 'Enable public booking page',
        contacts: 'Contact methods', copy: 'Copy Link', copied: '✓ Copied!', qr: 'Download QR',
        confirmTitle: 'Disabling the public booking page',
        confirmBody: 'Disabling the public booking page will make your /book/ URL stop working immediately. QR codes and links you have shared will return an error. Continue?',
        confirmStay: 'Cancel', confirmGo: 'Continue',
        appearance: 'Page appearance',
        taglineLbl: 'Tagline', taglinePh: 'e.g. A quiet 18-room retreat overlooking the Aegean',
        phoneLbl: 'Hotel phone', phonePh: '+30 25910 12345',
        phoneHelp: 'Shown on the confirmation screen so guests can reach you quickly.',
        mapsLbl: 'Google Maps link', mapsPh: 'https://maps.app.goo.gl/…',
        reviewsLbl: 'Google Reviews', reviewsCount: 'Number of reviews', reviewsRating: 'Rating (0-5)',
        currencyLbl: 'Currency', currencyPh: 'EUR',
        heroEyebrow: 'Custom hero photo',
        heroState1Body: "Want your own photo on your booking page? Send 2-3 high-quality photos to {email} with your hotel name in the subject line. We'll review and apply the best one within 2-3 business days.",
        heroState1Status: 'Your booking page currently uses the default Tavelo hero.',
        heroState2BodyChange: 'Want to change it? Send new photos to {email}.',
        heroRevertLink: 'Use the default Tavelo hero instead',
        heroConfirmTitle: 'Revert to the default hero',
        heroConfirmBody: 'This will remove the photo from your booking page. Guests will see the default Tavelo hero. Continue?',
        heroConfirmCancel: 'Cancel',
        heroConfirmGo: 'Revert to default',
      };

  const CONTACT_LABELS = lang === 'bg'
    ? { phone: 'Телефон', whatsapp: 'WhatsApp', viber: 'Viber', email: 'Имейл' }
    : { phone: 'Phone',   whatsapp: 'WhatsApp', viber: 'Viber', email: 'Email' };

  useEffect(() => {
    if (!directUrl) return;
    import('qrcode').then(QRCode => {
      QRCode.default.toDataURL(directUrl, { margin: 2, width: 240 }).then(url => setQrDataUrl(url));
    });
  }, [directUrl]);

  function bump() { markDirty('bookingPage', true); }

  function fillVars(s, vars) {
    return s.replace(/\{(\w+)\}/g, (_, k) => vars[k] != null ? String(vars[k]) : '');
  }

  function copyUrl() {
    navigator.clipboard.writeText(directUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  function downloadQr() {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = (property?.slug || 'hotel') + '-booking-qr.png';
    a.click();
  }
  function toggleContact(k) {
    setContacts(contacts.includes(k) ? contacts.filter(c => c !== k) : [...contacts, k]);
    bump();
  }
  function onEnableChange(v) {
    if (!v && (property?.has_booking_page ?? true)) {
      // Turning OFF — destructive, confirm on save
      setConfirmDisable(true);
    }
    setEnabled(v);
    bump();
  }

  function performSave() {
    const countNum = reviewsCount.trim() ? Math.max(0, parseInt(reviewsCount, 10) || 0) : null;
    const ratingNum = reviewsRating.trim() ? Math.max(0, Math.min(5, parseFloat(reviewsRating) || 0)) : null;
    saveSection('bookingPage', {
      has_booking_page: enabled,
      booking_page_contacts: contacts,
      public_tagline: tagline.trim() || null,
      phone: hotelPhone.trim() || null,
      google_maps_link: mapsLink.trim() || null,
      google_reviews_count: countNum,
      google_reviews_rating: ratingNum,
      currency: currency.trim() || 'EUR',
    });
  }
  async function revertHeroToDefault() {
    setRevertingHero(true);
    try {
      await onSave({ hero_image_url: null });
    } finally {
      setRevertingHero(false);
      setConfirmRevertHero(false);
    }
  }

  function onSaveClick() {
    if (!enabled && (property?.has_booking_page ?? true)) {
      setConfirmDisable(true);
      return;
    }
    performSave();
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: CL.stone, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1, display: 'block' };
  const inputStyle = { width: '100%', padding: '9px 11px', border: `1.5px solid ${CL.goldL}`, borderRadius: 9, fontSize: 14, fontFamily: FONT.body, background: '#FFFEF9', color: CL.bark, boxSizing: 'border-box', outline: 'none' };
  const helpStyle = { fontSize: 11, color: CL.stone, marginTop: 4, lineHeight: 1.4 };
  const eyebrowStyle = { fontSize: 9.5, fontWeight: 700, color: CL.stone, letterSpacing: 1.6, textTransform: 'uppercase', margin: '18px 0 10px', display: 'flex', alignItems: 'center', gap: 8 };

  // hero_image_url is stored as a root-relative path (e.g. /heroes/slug.webp) whose asset
  // lives in the booking project, served from book.tavelo.app. Resolve it against the booking
  // origin so this preview loads here on my.tavelo.app too. directUrl is book.tavelo.app/<slug>;
  // new URL() discards its path for root-relative inputs and leaves absolute URLs untouched.
  const heroPreviewSrc = property?.hero_image_url
    ? (() => { try { return new URL(property.hero_image_url, directUrl).href; } catch { return property.hero_image_url; } })()
    : null;
  // Reviews shown over the hero — mirrors BookingPage.jsx Hero (showReviews gate + saved values).
  const heroReviewsCount  = property?.google_reviews_count;
  const heroReviewsRating = property?.google_reviews_rating;
  const heroShowReviews   = heroReviewsCount != null && heroReviewsRating != null;

  return (
    <div style={{ paddingTop: 12 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={e => onEnableChange(e.target.checked)} />
        <span style={{ fontSize: 14, fontWeight: 600, color: CL.bark }}>{L.enable}</span>
      </label>

      {enabled && directUrl && (
        <>
          <div style={{ background: '#E8EFDE', border: '1px solid ' + CL.olive, borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: CL.bark, wordBreak: 'break-all', marginBottom: 10, background: '#fff', borderRadius: 8, padding: '8px 10px' }}>{directUrl}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={copyUrl}
                style={{ flex: 1, padding: '8px 0', background: copied ? CL.olive : CL.bark, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {copied ? L.copied : L.copy}
              </button>
              {qrDataUrl && (
                <button onClick={downloadQr}
                  style={{ flex: 1, padding: '8px 0', background: '#fff', color: CL.bark, border: '1px solid ' + CL.gold, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {L.qr}
                </button>
              )}
            </div>
            {qrDataUrl && <img src={qrDataUrl} alt="QR code" style={{ display: 'block', margin: '12px auto 0', width: 120, height: 120, borderRadius: 8 }} />}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{L.contacts}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {['phone','whatsapp','viber','email'].map(k => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={contacts.includes(k)} onChange={() => toggleContact(k)} />
                  {CONTACT_LABELS[k]}
                </label>
              ))}
            </div>
          </div>

          {/* ── Page appearance — fields the rebuilt booking page renders. ───── */}
          <div style={eyebrowStyle}>
            <span style={{ display: 'inline-block', width: 5, height: 5, background: CL.gold, transform: 'rotate(45deg)' }} />
            {L.appearance}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{L.taglineLbl}</label>
            <input type="text" value={tagline} placeholder={L.taglinePh}
              onChange={e => { setTagline(e.target.value); bump(); }} style={inputStyle} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{L.phoneLbl}</label>
            <input type="tel" value={hotelPhone} placeholder={L.phonePh}
              onChange={e => { setHotelPhone(e.target.value); bump(); }} style={inputStyle} />
            <div style={helpStyle}>{L.phoneHelp}</div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{L.mapsLbl}</label>
            <input type="url" value={mapsLink} placeholder={L.mapsPh}
              onChange={e => { setMapsLink(e.target.value); bump(); }} style={inputStyle} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{L.reviewsLbl}</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <input type="number" min="0" step="1" value={reviewsCount} placeholder={L.reviewsCount}
                  onChange={e => { setReviewsCount(e.target.value); bump(); }} style={inputStyle} />
                <div style={{ ...helpStyle, marginTop: 3 }}>{L.reviewsCount}</div>
              </div>
              <div style={{ flex: 1 }}>
                <input type="number" min="0" max="5" step="0.1" value={reviewsRating} placeholder="4.8"
                  onChange={e => { setReviewsRating(e.target.value); bump(); }} style={inputStyle} />
                <div style={{ ...helpStyle, marginTop: 3 }}>{L.reviewsRating}</div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{L.currencyLbl}</label>
            <input type="text" value={currency} placeholder={L.currencyPh} maxLength={3}
              onChange={e => { setCurrency(e.target.value.toUpperCase()); bump(); }}
              style={{ ...inputStyle, width: 80 }} />
          </div>

          {/* ── Custom hero photo (J11) ────────────────────────────────────── */}
          <div style={eyebrowStyle}>
            <span style={{ display: 'inline-block', width: 5, height: 5, background: CL.gold, transform: 'rotate(45deg)' }} />
            {L.heroEyebrow}
          </div>

          {property?.hero_image_url ? (
            <div style={{ marginBottom: 14 }}>
              {/* 1:1 replica of the booking page hero (BookingPage.jsx Hero photo variant):
                  same maxWidth 480 + height 340 footprint, so object-fit:cover produces the
                  identical crop at any width, and the same gradient / name / gold rule / reviews.
                  This preview shows exactly what guests see. */}
              <div style={{
                position: 'relative',
                width: '100%', maxWidth: 480, height: 340, borderRadius: 10, overflow: 'hidden',
                border: '1px solid ' + CL.goldL, margin: '0 auto 10px', background: CL.olive,
              }}>
                <img
                  src={heroPreviewSrc}
                  alt=""
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none',
                  background: 'linear-gradient(180deg, rgba(46,58,40,0) 35%, rgba(46,58,40,0.55) 100%)',
                }} />
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
                  textAlign: 'center', padding: '36px 28px 30px', color: '#fff',
                }}>
                  <div style={{
                    fontFamily: FONT.display, fontWeight: 500, fontSize: 42, letterSpacing: '0.4px',
                    color: '#FFFEF9', lineHeight: 1.05, marginBottom: 10,
                    textShadow: '0 2px 16px rgba(20,30,15,0.45)',
                  }}>{property?.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ height: 1, width: 36, background: CL.gold }} />
                    <span style={{ width: 6, height: 6, background: CL.gold, transform: 'rotate(45deg)', display: 'inline-block' }} />
                    <span style={{ height: 1, width: 36, background: CL.gold }} />
                  </div>
                  {heroShowReviews && (
                    <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: CL.gold, fontWeight: 600 }}>
                      <span style={{ color: CL.gold, letterSpacing: 1 }}>★★★★★</span>
                      <span style={{ color: '#FFFEF9', fontWeight: 500 }}>
                        {Number(heroReviewsRating).toFixed(1)} · {heroReviewsCount} Google reviews
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 13, color: CL.bark, lineHeight: 1.45, marginBottom: 10 }}>
                {fillVars(L.heroState2BodyChange, { email: TAVELO_CONTACT_EMAIL })
                  .split(TAVELO_CONTACT_EMAIL).map((part, i, arr) => (
                  <span key={i}>
                    {part}
                    {i < arr.length - 1 && (
                      <a href={`mailto:${TAVELO_CONTACT_EMAIL}`}
                        style={{ color: CL.bark, fontWeight: 700, textDecoration: 'underline' }}>
                        {TAVELO_CONTACT_EMAIL}
                      </a>
                    )}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setConfirmRevertHero(true)}
                disabled={revertingHero}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: CL.stone, fontSize: 12, textDecoration: 'underline',
                  cursor: revertingHero ? 'default' : 'pointer', fontFamily: 'inherit',
                }}
              >
                {L.heroRevertLink}
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: CL.bark, lineHeight: 1.45, marginBottom: 8 }}>
                {fillVars(L.heroState1Body, { email: TAVELO_CONTACT_EMAIL })
                  .split(TAVELO_CONTACT_EMAIL).map((part, i, arr) => (
                  <span key={i}>
                    {part}
                    {i < arr.length - 1 && (
                      <a href={`mailto:${TAVELO_CONTACT_EMAIL}`}
                        style={{ color: CL.bark, fontWeight: 700, textDecoration: 'underline' }}>
                        {TAVELO_CONTACT_EMAIL}
                      </a>
                    )}
                  </span>
                ))}
              </div>
              <div style={{ ...helpStyle, marginTop: 0 }}>
                {L.heroState1Status}
              </div>
            </div>
          )}
        </>
      )}

      <button onClick={onSaveClick}
        style={{ width: '100%', padding: 12, background: CL.bark, color: CL.gold, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        {tS.save}
      </button>

      {confirmDisable && (
        <ConfirmModal
          title={L.confirmTitle}
          body={L.confirmBody}
          cancelLabel={L.confirmStay}
          confirmLabel={L.confirmGo}
          onCancel={() => { setConfirmDisable(false); setEnabled(true); }}
          onConfirm={() => { setConfirmDisable(false); performSave(); }}
        />
      )}
      {confirmRevertHero && (
        <ConfirmModal
          title={L.heroConfirmTitle}
          body={L.heroConfirmBody}
          cancelLabel={L.heroConfirmCancel}
          confirmLabel={L.heroConfirmGo}
          onCancel={() => setConfirmRevertHero(false)}
          onConfirm={revertHeroToDefault}
        />
      )}
    </div>
  );
}
function BankSection({ property, lang, tS, showToast, saveSection, markDirty }) {
  const country = property?.country ?? 'GR';
  const isUk = country === 'GB';

  const [holder, setHolder]       = useState(property?.bank_account_holder ?? '');
  const [bankName, setBankName]   = useState(property?.bank_name ?? '');
  const [ibanRaw, setIbanRaw]     = useState(property?.bank_iban ?? '');
  const [ibanDisplay, setIbanDisplay] = useState(() => {
    const p = parseIban(property?.bank_iban ?? '', country);
    return p.ok ? p.formatted : (property?.bank_iban ?? '');
  });
  const [bic, setBic]             = useState(property?.bank_bic ?? '');
  const [ukHolder, setUkHolder]   = useState(property?.bank_account_holder ?? '');
  const [ukAccount, setUkAccount] = useState(property?.bank_uk_account_number ?? '');
  const ukSortStored = property?.bank_uk_sort_code ?? '';
  const [sort1, setSort1] = useState(ukSortStored.slice(0, 2));
  const [sort2, setSort2] = useState(ukSortStored.slice(2, 4));
  const [sort3, setSort3] = useState(ukSortStored.slice(4, 6));
  const [errs, setErrs] = useState({});

  const L = lang === 'bg'
    ? {
        ukBlock: 'Вътрешна UK сметка', intlBlock: 'Международна сметка',
        holder: 'Титуляр', bankName: 'Банка', iban: 'IBAN', bic: 'BIC / SWIFT',
        account: 'Номер на сметка', sort: 'Sort code',
        hint: 'Гостите от UK използват вътрешната сметка; останалите, международната. Шаблонът за депозит включва и двата блока когато са попълнени.',
        ibanErr: 'IBAN е невалиден', bicErr: 'BIC е невалиден', sortErr: 'Sort code = 6 цифри', acctErr: 'Номер = 6–10 цифри',
      }
    : {
        ukBlock: 'UK Domestic', intlBlock: 'International',
        holder: 'Account holder', bankName: 'Bank name', iban: 'IBAN', bic: 'BIC / SWIFT',
        account: 'Account number', sort: 'Sort code',
        hint: 'Guests paying from the UK use the domestic details. Others use international. The default deposit-request message includes both when both are filled.',
        ibanErr: 'IBAN is invalid', bicErr: 'BIC is invalid', sortErr: 'Sort code must be 6 digits', acctErr: 'Account number must be 6–10 digits',
      };

  function bump() { markDirty('bank', true); }

  function handleIbanBlur() {
    if (!ibanRaw) { setIbanDisplay(''); setErrs(e => ({ ...e, iban: false })); return; }
    const p = parseIban(ibanRaw, country);
    if (p.ok) { setIbanDisplay(p.formatted); setIbanRaw(p.normalised); setErrs(e => ({ ...e, iban: false })); }
    else      { setErrs(e => ({ ...e, iban: true })); }
  }

  function onSaveClick() {
    const nextErrs = {};

    let normalisedIban = null;
    if (ibanRaw) {
      const p = parseIban(ibanRaw, country);
      if (p.ok) normalisedIban = p.normalised;
      else nextErrs.iban = true;
    }

    let normalisedBic = null;
    if (bic) {
      if (validateBic(bic)) normalisedBic = bic.toUpperCase();
      else nextErrs.bic = true;
    }

    let sortCode = null, accountNumber = null;
    if (isUk) {
      const sortRaw = (sort1 + sort2 + sort3).trim();
      if (sortRaw) {
        if (validateSortCode(sortRaw)) sortCode = sortRaw;
        else nextErrs.sort = true;
      }
      if (ukAccount) {
        if (/^\d{6,10}$/.test(ukAccount)) accountNumber = ukAccount;
        else nextErrs.account = true;
      }
    }

    if (Object.keys(nextErrs).length > 0) {
      setErrs(nextErrs);
      showToast(tS.fixHighlighted);
      return;
    }
    setErrs({});

    const updates = {
      bank_account_holder: (isUk ? ukHolder : holder).trim() || null,
      bank_name: bankName.trim() || null,
      bank_iban: normalisedIban,
      bank_bic: normalisedBic,
    };
    if (isUk) {
      updates.bank_uk_account_number = accountNumber;
      updates.bank_uk_sort_code = sortCode;
    }
    saveSection('bank', updates);
  }

  const labelStyle = { fontSize: 11, fontWeight: 700, color: CL.stone, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1, display: 'block' };
  const inputStyle = (err) => ({ width: '100%', padding: '10px 12px', border: '1px solid ' + (err ? CL.terra : CL.goldL), borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff', color: CL.bark, fontFamily: 'inherit' });
  const errText = (msg) => <div style={{ fontSize: 11, color: CL.terra, marginTop: 4 }}>{msg}</div>;

  function SortCodeInputs() {
    function onBoxInput(setter, next, e) {
      const v = e.target.value.replace(/\D/g, '').slice(0, 2);
      setter(v);
      bump();
      if (v.length === 2 && next) next.focus();
    }
    const refs = [null, null, null];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input ref={r => refs[0] = r} value={sort1} onChange={e => onBoxInput(setSort1, refs[1], e)}
          maxLength={2} inputMode="numeric" style={{ ...inputStyle(errs.sort), width: 60, textAlign: 'center' }} />
        <span>-</span>
        <input ref={r => refs[1] = r} value={sort2} onChange={e => onBoxInput(setSort2, refs[2], e)}
          maxLength={2} inputMode="numeric" style={{ ...inputStyle(errs.sort), width: 60, textAlign: 'center' }} />
        <span>-</span>
        <input ref={r => refs[2] = r} value={sort3} onChange={e => onBoxInput(setSort3, null, e)}
          maxLength={2} inputMode="numeric" style={{ ...inputStyle(errs.sort), width: 60, textAlign: 'center' }} />
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 12 }}>
      {isUk && (
        <>
          {/* UK Domestic */}
          <div style={{ background: CL.sand, borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: CL.bark, marginBottom: 10 }}>{L.ukBlock}</div>

            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{L.holder}</label>
              <input value={ukHolder} onChange={e => { setUkHolder(e.target.value); bump(); }} style={inputStyle(false)} />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{L.account}</label>
              <input value={ukAccount}
                onChange={e => { setUkAccount(e.target.value.replace(/\D/g, '').slice(0, 10)); bump(); }}
                inputMode="numeric" style={inputStyle(errs.account)} />
              {errs.account && errText(L.acctErr)}
            </div>

            <div style={{ marginBottom: 4 }}>
              <label style={labelStyle}>{L.sort}</label>
              <SortCodeInputs />
              {errs.sort && errText(L.sortErr)}
            </div>
          </div>

          {/* International */}
          <div style={{ borderRadius: 8, padding: 0, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: CL.bark, marginBottom: 10 }}>{L.intlBlock}</div>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{L.holder}</label>
              <input value={holder} onChange={e => { setHolder(e.target.value); bump(); }} style={inputStyle(false)} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{L.bankName}</label>
              <input value={bankName} onChange={e => { setBankName(e.target.value); bump(); }} style={inputStyle(false)} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{L.iban}</label>
              <input value={ibanDisplay}
                onChange={e => { setIbanRaw(e.target.value); setIbanDisplay(e.target.value); bump(); }}
                onBlur={handleIbanBlur}
                style={inputStyle(errs.iban)} />
              {errs.iban && errText(L.ibanErr)}
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{L.bic}</label>
              <input value={bic}
                onChange={e => { setBic(e.target.value.toUpperCase()); bump(); }}
                style={inputStyle(errs.bic)} />
              {errs.bic && errText(L.bicErr)}
            </div>
          </div>

          <p style={{ fontSize: 11, color: CL.stone, marginBottom: 14 }}>{L.hint}</p>
        </>
      )}

      {!isUk && (
        <>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>{L.holder}</label>
            <input value={holder} onChange={e => { setHolder(e.target.value); bump(); }} style={inputStyle(false)} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>{L.bankName}</label>
            <input value={bankName} onChange={e => { setBankName(e.target.value); bump(); }} style={inputStyle(false)} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>{L.iban}</label>
            <input value={ibanDisplay}
              onChange={e => { setIbanRaw(e.target.value); setIbanDisplay(e.target.value); bump(); }}
              onBlur={handleIbanBlur}
              style={inputStyle(errs.iban)} />
            {errs.iban && errText(L.ibanErr)}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>{L.bic}</label>
            <input value={bic}
              onChange={e => { setBic(e.target.value.toUpperCase()); bump(); }}
              style={inputStyle(errs.bic)} />
            {errs.bic && errText(L.bicErr)}
          </div>
        </>
      )}

      <button onClick={onSaveClick}
        style={{ width: '100%', padding: 12, background: CL.bark, color: CL.gold, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        {tS.save}
      </button>
    </div>
  );
}
function DepositPolicySection({ property, lang, tS, showToast, saveSection, markDirty }) {
  const [mode, setMode]   = useState(property?.deposit_policy_mode ?? 'first_night');
  const [value, setValue] = useState(
    property?.deposit_policy_value != null ? String(property.deposit_policy_value) : ''
  );
  const [errs, setErrs]   = useState({});

  const cur = curSym(property?.currency || 'EUR');

  const L = lang === 'bg'
    ? {
        helper: 'Каква сума да искаме от госта като депозит, когато натиснете действие за искане на депозит?',
        firstNight: 'Цена за първа нощувка',
        percentage: 'Процент от общата сума',
        fixed:      'Фиксирана сума',
        manual:     'Питай ръчно всеки път',
        firstNightHint: 'Цената на стаята за една нощ от резервацията',
        percentageHint: 'Процент от (цена за нощ × брой нощувки)',
        pctErr:    'Процент трябва да е между 1 и 100',
        amountErr: 'Сумата трябва да е положителна',
      }
    : {
        helper: 'How much should we ask the guest to send as a deposit when you tap a deposit-request action?',
        firstNight: "First night's rate",
        percentage: 'Percentage of total',
        fixed:      'Fixed amount',
        manual:     'Ask manually each time',
        firstNightHint: 'The booking room rate for one night',
        percentageHint: 'Percentage of (price per night × nights)',
        pctErr:    'Percentage must be between 1 and 100',
        amountErr: 'Amount must be positive',
      };

  function bump() { markDirty('deposit', true); }

  function onSaveClick() {
    const nextErrs = {};
    let normalisedValue = null;

    if (mode === 'percentage') {
      const n = Number(value);
      if (!Number.isFinite(n) || n < 1 || n > 100) nextErrs.value = L.pctErr;
      else normalisedValue = n;
    } else if (mode === 'fixed') {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) nextErrs.value = L.amountErr;
      else normalisedValue = n;
    }

    if (Object.keys(nextErrs).length > 0) {
      setErrs(nextErrs);
      showToast(tS.fixHighlighted);
      return;
    }
    setErrs({});

    saveSection('deposit', {
      deposit_policy_mode: mode,
      deposit_policy_value: normalisedValue,
    });
  }

  const radioRow = (key, label, hint) => (
    <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', cursor: 'pointer' }}>
      <input
        type="radio"
        name="deposit-policy-mode"
        checked={mode === key}
        onChange={() => { setMode(key); bump(); }}
        style={{ marginTop: 3, accentColor: CL.bark }}
      />
      <span>
        <span style={{ display: 'block', fontSize: 14, color: CL.bark, fontWeight: 600 }}>{label}</span>
        {hint && <span style={{ display: 'block', fontSize: 12, color: CL.stone, marginTop: 2, lineHeight: 1.45 }}>{hint}</span>}
      </span>
    </label>
  );

  const inputStyle = (err) => ({
    width: 120, padding: '8px 10px',
    border: '1px solid ' + (err ? CL.terra : CL.goldL),
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
    background: '#fff', color: CL.bark, fontFamily: 'inherit',
  });

  return (
    <div style={{ padding: '8px 0 4px' }}>
      <p style={{ fontSize: 12, color: CL.stone, lineHeight: 1.5, marginTop: 0, marginBottom: 12 }}>
        {L.helper}
      </p>

      {radioRow('first_night', L.firstNight, L.firstNightHint)}

      <div>
        {radioRow('percentage', L.percentage, L.percentageHint)}
        {mode === 'percentage' && (
          <div style={{ marginLeft: 26, marginBottom: 8 }}>
            <input
              type="number" inputMode="numeric" min={1} max={100}
              value={value}
              onChange={e => { setValue(e.target.value); bump(); }}
              style={inputStyle(errs.value)}
            />
            <span style={{ marginLeft: 6, color: CL.stone, fontSize: 14 }}>%</span>
            {errs.value && <div style={{ fontSize: 11, color: CL.terra, marginTop: 4 }}>{errs.value}</div>}
          </div>
        )}
      </div>

      <div>
        {radioRow('fixed', L.fixed)}
        {mode === 'fixed' && (
          <div style={{ marginLeft: 26, marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: CL.stone, fontSize: 14 }}>{cur}</span>
            <input
              type="number" inputMode="decimal" min={0}
              value={value}
              onChange={e => { setValue(e.target.value); bump(); }}
              style={inputStyle(errs.value)}
            />
            {errs.value && <div style={{ fontSize: 11, color: CL.terra, marginTop: 4, marginLeft: 8 }}>{errs.value}</div>}
          </div>
        )}
      </div>

      {radioRow('manual', L.manual)}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <button
          onClick={onSaveClick}
          style={{ padding: '10px 18px', background: CL.bark, color: CL.cream, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
        >
          {tS.save}
        </button>
      </div>
    </div>
  );
}
function GuestCommSection({ property, lang, tS, showToast, saveSection, markDirty }) {
  const enabled = property?.guest_comm_enabled !== false;
  function toggleMaster() {
    const turningOn = !enabled;
    const updates = { guest_comm_enabled: turningOn };
    // Stamp the start-date the first time messaging is switched on. From then on
    // the tracker skips confirmation/deposit reminders for bookings made before
    // this moment, so enabling the panel never surfaces a historical backlog.
    if (turningOn && !property?.guest_comm_enabled_at) {
      updates.guest_comm_enabled_at = new Date().toISOString();
    }
    saveSection('guestCommMaster', updates);
  }

  // Linked data fields used by message templates.
  const dataFields = [
    { label: lang === 'bg' ? 'Адрес на хотела' : 'Hotel address', filled: !!property?.address, usedBy: 'arrival_reminder' },
    { label: lang === 'bg' ? 'Час за настаняване' : 'Check-in time', filled: !!property?.check_in_time, usedBy: 'arrival_reminder' },
    { label: lang === 'bg' ? 'Google Maps линк' : 'Google Maps link', filled: !!property?.google_maps_link, usedBy: 'arrival_reminder' },
    { label: lang === 'bg' ? 'Waze линк' : 'Waze link', filled: !!property?.waze_link, usedBy: 'arrival_reminder' },
    { label: lang === 'bg' ? 'Google Review линк' : 'Google Review link', filled: !!property?.google_review_link, usedBy: 'review_request' },
    { label: lang === 'bg' ? 'Банкови данни (IBAN + банка + титуляр)' : 'Bank details (IBAN + bank + holder)',
      filled: !!(property?.bank_iban && property?.bank_name && property?.bank_account_holder), usedBy: 'deposit_request' },
    { label: lang === 'bg' ? 'Stripe ключ' : 'Stripe key', filled: !!property?.stripe_restricted_key, usedBy: 'deposit_request' },
    { label: lang === 'bg' ? 'Линк за директни резервации' : 'Direct booking URL', filled: !!property?.slug, usedBy: 'confirmation (Booking.com suffix)' },
  ];

  return (
    <div style={{ paddingTop: 12 }}>
      {/* Master toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: CL.sand, border: '1px solid ' + CL.goldL, borderRadius: 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: CL.bark }}>
            {lang === 'bg' ? 'Използвай гост-комуникации' : 'Use guest communications'}
          </div>
          <div style={{ fontSize: 11, color: CL.stone, marginTop: 2 }}>
            {lang === 'bg' ? 'Кръгчета на резервациите + Действия днес' : 'Per-booking circles + Today actions feed'}
          </div>
        </div>
        <button onClick={toggleMaster}
          style={{ width: 48, height: 28, borderRadius: 14, background: enabled ? CL.olive : CL.goldL, border: 'none', cursor: 'pointer', position: 'relative' }}>
          <span style={{ position: 'absolute', top: 3, left: enabled ? 23 : 3, width: 22, height: 22, borderRadius: 11, background: '#fff', transition: 'left 0.15s' }} />
        </button>
      </div>

      {!enabled && (
        <div style={{ fontSize: 12, color: CL.stone, padding: '8px 14px', background: '#FBF6EC', border: '1px solid ' + CL.goldL, borderRadius: 8, marginBottom: 12 }}>
          {lang === 'bg' ? 'Изключено. Шаблоните и историята са запазени. Могат да бъдат включени отново по всяко време.' : 'Disabled. Templates and history are preserved — can be re-enabled any time.'}
        </div>
      )}

      {/* Templates (existing TemplatesSection, only when master is on) */}
      {enabled && (
        <>
          <div style={{ fontSize: 11, color: CL.stone, textTransform: 'uppercase', letterSpacing: 1, padding: '4px 0 6px', fontWeight: 700 }}>
            {lang === 'bg' ? 'Шаблони на съобщения' : 'Message templates'}
          </div>
          <TemplatesSection property={property} lang={lang} tS={tS} showToast={showToast} saveSection={saveSection} markDirty={markDirty} />
        </>
      )}

      {/* Linked data summary */}
      {enabled && (
        <>
          <div style={{ fontSize: 11, color: CL.stone, textTransform: 'uppercase', letterSpacing: 1, padding: '14px 0 6px', fontWeight: 700 }}>
            {lang === 'bg' ? 'Свързани данни' : 'Linked data'}
          </div>
          <div style={{ fontSize: 11, color: CL.stone, marginBottom: 8 }}>
            {lang === 'bg' ? 'Полета, които се вмъкват в шаблоните. Празните полета заключват съответното кръгче.' : 'Fields interpolated into templates. Empty fields lock the matching circle.'}
          </div>
          {dataFields.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: f.filled ? '#F5EFE6' : '#FFF3E0', border: '1px solid ' + (f.filled ? CL.goldL : CL.terra), borderRadius: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 13 }}>{f.filled ? '✓' : '⚠'}</span>
              <div style={{ flex: 1, fontSize: 12, color: CL.bark }}>{f.label}</div>
              <div style={{ fontSize: 10, color: CL.stone }}>{f.usedBy}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function TemplatesSection({ property, lang, tS, showToast, saveSection, markDirty }) {
  const TEMPLATE_KEYS = ['confirmation', 'deposit_request', 'balance_reminder', 'arrival_reminder', 'review_request'];
  const TEMPLATE_LABELS = {
    confirmation:     { en: 'Booking Confirmation', bg: 'Потвърждение за резервация' },
    deposit_request:  { en: 'Deposit Request',      bg: 'Искане на депозит' },
    balance_reminder: { en: 'Balance Reminder',     bg: 'Напомняне за баланс' },
    arrival_reminder: { en: 'Arrival Reminder',     bg: 'Напомняне за пристигане' },
    review_request:   { en: 'Review Request',       bg: 'Искане за отзив' },
  };
  const propertyLang = property?.lang ?? 'en';
  const isBilingual = propertyLang.includes(',');
  const primaryLang = propertyLang.split(',')[0] || 'en';
  const [active, setActive] = useState(null);
  const [tlang, setTlang] = useState(primaryLang);
  const [showVars, setShowVars] = useState(false);
  const textareaRef = useRef(null);
  const [texts, setTexts] = useState(() => {
    const out = {};
    TEMPLATE_KEYS.forEach(k => {
      out[k] = {
        en: property?.message_templates?.[k]?.en || DEFAULT_TEMPLATES[k].en,
        bg: property?.message_templates?.[k]?.bg || DEFAULT_TEMPLATES[k].bg,
      };
    });
    return out;
  });

  function bump() { markDirty('templates', true); }

  async function saveKey(key) {
    const nextTemplates = { ...(property?.message_templates || {}), [key]: texts[key] };
    saveSection('templates', { message_templates: nextTemplates });

    // Bilingual parity nudge — fires once per save when the *other* language still equals the default.
    if (isBilingual) {
      const otherLang = tlang === 'en' ? 'bg' : 'en';
      const otherText = texts[key][otherLang];
      const defaultText = DEFAULT_TEMPLATES[key][otherLang];
      if (otherText === defaultText) {
        showToast?.(lang === 'bg'
          ? `${otherLang.toUpperCase()} версията все още е по подразбиране. Обновете и нея.`
          : `${otherLang.toUpperCase()} version still uses the default — update it too.`);
      }
    }

    setActive(null);
  }

  // Plain-language pill groups, organised by what an owner is likely thinking about
  // when they pause mid-message. Tokens that exist but are rarely used standalone
  // (individual bank fields) are deliberately omitted — owners get the same data via
  // the friendly aggregate [bankDetails] / [paymentInstructions] tokens.
  const VARIABLE_GROUPS = lang === 'bg'
    ? [
        { label: 'Резервация', items: [
          { token: '[guestName]', label: 'Име на гост' },
          { token: '[hotelName]', label: 'Име на хотел' },
          { token: '[roomName]',  label: 'Стая' },
          { token: '[checkIn]',   label: 'Дата на настаняване' },
          { token: '[checkOut]',  label: 'Дата на напускане' },
          { token: '[nights]',    label: 'Брой нощувки' },
        ]},
        { label: 'Цени и плащане', items: [
          { token: '[totalPrice]',          label: 'Обща сума' },
          { token: '[depositAmount]',       label: 'Сума на депозит' },
          { token: '[balanceDue]',          label: 'Оставащ баланс' },
          { token: '[paymentInstructions]', label: 'Указания за плащане' },
          { token: '[bankDetails]',         label: 'Банкови данни' },
        ]},
        { label: 'Пристигане', items: [
          { token: '[hotelAddress]', label: 'Адрес' },
          { token: '[checkInTime]',  label: 'Час на настаняване' },
          { token: '[directions]',   label: 'Указания (Maps + Waze)' },
          { token: '[breakfast]',    label: 'Информация за закуска' },
        ]},
        { label: 'Линкове', items: [
          { token: '[googleReviewLink]', label: 'Линк за отзив в Google' },
          { token: '[googleMapsLink]',   label: 'Google Maps' },
          { token: '[wazeLink]',         label: 'Waze' },
          { token: '[directBookingUrl]', label: 'Линк за директна резервация' },
        ]},
      ]
    : [
        { label: 'Booking', items: [
          { token: '[guestName]', label: 'Guest name' },
          { token: '[hotelName]', label: 'Hotel name' },
          { token: '[roomName]',  label: 'Room' },
          { token: '[checkIn]',   label: 'Check-in date' },
          { token: '[checkOut]',  label: 'Check-out date' },
          { token: '[nights]',    label: 'Nights' },
        ]},
        { label: 'Pricing & payment', items: [
          { token: '[totalPrice]',          label: 'Total price' },
          { token: '[depositAmount]',       label: 'Deposit amount' },
          { token: '[balanceDue]',          label: 'Balance due' },
          { token: '[paymentInstructions]', label: 'Payment details' },
          { token: '[bankDetails]',         label: 'Bank details' },
        ]},
        { label: 'Arrival', items: [
          { token: '[hotelAddress]', label: 'Address' },
          { token: '[checkInTime]',  label: 'Check-in time' },
          { token: '[directions]',   label: 'Directions (Maps + Waze)' },
          { token: '[breakfast]',    label: 'Breakfast info' },
        ]},
        { label: 'Links', items: [
          { token: '[googleReviewLink]', label: 'Google review link' },
          { token: '[googleMapsLink]',   label: 'Google Maps' },
          { token: '[wazeLink]',         label: 'Waze' },
          { token: '[directBookingUrl]', label: 'Direct booking link' },
        ]},
      ];

  function insertAtCursor(token, key) {
    const ta = textareaRef.current;
    const cur = texts[key][tlang];
    if (!ta) {
      setTexts(prev => ({ ...prev, [key]: { ...prev[key], [tlang]: cur + token } }));
      bump();
      return;
    }
    const start = ta.selectionStart ?? cur.length;
    const end = ta.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    setTexts(prev => ({ ...prev, [key]: { ...prev[key], [tlang]: next } }));
    bump();
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  // Sample booking + room used to render the live preview. Recognisable values so
  // brackets visibly become real words on the same screen.
  const SAMPLE = lang === 'bg'
    ? {
        booking: {
          guestName: 'Мария Петрова',
          checkIn: '2026-07-14', checkOut: '2026-07-17',
          price: 360, depositAmount: 100,
          breakfastIncluded: true, breakfastPrice: 0,
          phone: '+30 6912 345 678',
        },
        room: { name: 'Двойна стая' },
      }
    : {
        booking: {
          guestName: 'Maria Smith',
          checkIn: '2026-07-14', checkOut: '2026-07-17',
          price: 360, depositAmount: 100,
          breakfastIncluded: true, breakfastPrice: 0,
          phone: '+30 6912 345 678',
        },
        room: { name: 'Double room' },
      };

  function renderPreview(key) {
    const raw = texts[key][tlang];
    try {
      const vars = buildTemplateVars(SAMPLE.booking, SAMPLE.room, property, tlang, {});
      return interpolateTemplate(raw, vars);
    } catch {
      return raw;
    }
  }

  return (
    <div style={{ paddingTop: 12 }}>
      {TEMPLATE_KEYS.map(key => (
        <div key={key}>
          {active !== key ? (
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, marginBottom: 8 }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const enabledMap = { ...(property?.message_template_enabled || {}) };
                  enabledMap[key] = !(enabledMap[key] !== false); // toggle (default true)
                  saveSection('templateToggle', { message_template_enabled: enabledMap });
                }}
                title={(property?.message_template_enabled?.[key] !== false) ? (lang === 'bg' ? 'Активен, натиснете за изключване' : 'Enabled — tap to disable') : (lang === 'bg' ? 'Изключен, натиснете за активиране' : 'Disabled — tap to enable')}
                style={{
                  flexShrink: 0, width: 36,
                  background: (property?.message_template_enabled?.[key] !== false) ? CL.olive : CL.goldL,
                  color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 16, fontWeight: 700,
                }}>
                {(property?.message_template_enabled?.[key] !== false) ? '✓' : '·'}
              </button>
              <button onClick={() => setActive(key)}
                style={{ flex: 1, textAlign: 'left', padding: '10px 12px', background: CL.sand, border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 13, fontWeight: 600, color: CL.bark, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {TEMPLATE_LABELS[key][lang === 'bg' ? 'bg' : 'en']}
                <span style={{ fontSize: 11, color: CL.stone }}>{lang === 'bg' ? 'Промени →' : 'Edit →'}</span>
              </button>
            </div>
          ) : (
            <div style={{ border: '1px solid ' + CL.gold, borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: CL.bark }}>{TEMPLATE_LABELS[key][lang === 'bg' ? 'bg' : 'en']}</div>
                {isBilingual && (
                  <div style={{ display: 'flex', background: CL.sand, borderRadius: 6, overflow: 'hidden' }}>
                    {['en','bg'].map(l => (
                      <button key={l} onClick={() => setTlang(l)}
                        style={{ padding: '3px 8px', border: 'none', background: tlang === l ? CL.bark : 'transparent', color: tlang === l ? CL.cream : CL.stone, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        {l.toUpperCase()}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <textarea
                ref={textareaRef}
                value={texts[key][tlang]}
                onChange={e => {
                  setTexts(prev => ({ ...prev, [key]: { ...prev[key], [tlang]: e.target.value } }));
                  bump();
                }}
                rows={10}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: FONT.body, lineHeight: 1.5, background: '#fff', color: CL.bark }}
              />

              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowVars(v => !v)}
                  style={{ background: 'transparent', border: 'none', padding: 0, fontSize: 12, color: CL.stone, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {lang === 'bg' ? 'Какво мога да добавя?' : 'What can I add?'}
                  <span style={{ fontSize: 10 }}>{showVars ? '▴' : '▾'}</span>
                </button>
                {showVars && (
                  <div style={{ marginTop: 8, padding: 10, background: CL.sand, borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: CL.stone, marginBottom: 8 }}>
                      {lang === 'bg' ? 'Натиснете, за да вмъкнете там, където е курсорът.' : 'Tap to insert at the cursor.'}
                    </div>
                    {VARIABLE_GROUPS.map(group => (
                      <div key={group.label} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: CL.stone, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{group.label}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {group.items.map(item => (
                            <button
                              key={item.token}
                              type="button"
                              onClick={() => insertAtCursor(item.token, key)}
                              style={{ padding: '4px 8px', background: '#fff', border: '1px solid ' + CL.goldL, borderRadius: 999, fontSize: 11, color: CL.bark, cursor: 'pointer' }}>
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: CL.stone, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  {lang === 'bg' ? 'Преглед: какво ще получи гостът' : 'Preview — what your guest will receive'}
                </div>
                <div style={{ padding: 12, background: CL.cream, border: '1px dashed ' + CL.goldL, borderRadius: 8, fontSize: 12, lineHeight: 1.5, color: CL.bark, whiteSpace: 'pre-wrap', fontFamily: FONT.body }}>
                  {renderPreview(key)}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={() => saveKey(key)}
                  style={{ flex: 2, padding: '8px 0', background: CL.bark, color: CL.gold, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {tS.save}
                </button>
                <button onClick={() => setActive(null)}
                  style={{ flex: 1, padding: '8px 0', background: '#fff', color: CL.stone, border: '1px solid ' + CL.goldL, borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                  {lang === 'bg' ? 'Отказ' : 'Cancel'}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PaymentsSection({ property, lang, tS, saveSection, markDirty }) {
  const [stripeEnabled, setStripeEnabled] = useState(!!property?.stripe_restricted_key);
  const [stripeKey, setStripeKey]         = useState(property?.stripe_restricted_key ?? '');
  const [saved, setSaved]                 = useState(false);

  async function handleSave() {
    await saveSection('payments', { stripe_restricted_key: stripeEnabled ? (stripeKey.trim() || null) : null });
    if (!stripeEnabled) setStripeKey('');
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ padding: '14px 16px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <input
          type="checkbox"
          id="stripe-enabled"
          checked={stripeEnabled}
          onChange={e => { setStripeEnabled(e.target.checked); if (!e.target.checked) setStripeKey(''); markDirty('payments'); }}
          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: CL.bark }}
        />
        <label htmlFor="stripe-enabled" style={{ fontSize: 14, fontWeight: 600, color: CL.bark, cursor: 'pointer' }}>
          {lang === 'bg' ? 'Приемане на плащания с карта (Stripe)' : 'Accept card payments via Stripe'}
        </label>
      </div>
      {stripeEnabled && (
      <div style={{ marginBottom: 12 }}>
        <label style={st.lbl}>Stripe Restricted API Key</label>
        <input
          type="password"
          value={stripeKey}
          onChange={e => { setStripeKey(e.target.value); markDirty('payments'); }}
          placeholder="rk_live_..."
          style={{ ...st.inp, fontFamily: 'monospace', fontSize: 13 }}
        />
        <div style={{ fontSize: 12, color: CL.stone, marginTop: 8, lineHeight: 1.6, background: CL.sand, borderRadius: 8, padding: '10px 12px' }}>
          {lang === 'bg' ? (
            <>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Как да получите ключ:</div>
              <ol style={{ margin: 0, paddingLeft: 16 }}>
                <li>Влезте в акаунта си на <strong>stripe.com</strong></li>
                <li>Отидете на <strong>Developers → API Keys</strong></li>
                <li>Натиснете <strong>"Create restricted key"</strong></li>
                <li>Дайте му произволно име (напр. "Tavelo")</li>
                <li>В Permissions задайте <strong>Products</strong>, <strong>Prices</strong> и <strong>Payment Links</strong> на <strong>Write</strong></li>
                <li>Натиснете Create, копирайте ключа и го поставете тук</li>
              </ol>
              <div style={{ marginTop: 8, color: CL.bark }}>Използвайте тестов ключ (<em>rk_test_...</em>) докато тествате, и реален (<em>rk_live_...</em>) за истински плащания. Оставете полето празно за да изключите.</div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>How to get your key:</div>
              <ol style={{ margin: 0, paddingLeft: 16 }}>
                <li>Log in to your Stripe account at <strong>stripe.com</strong></li>
                <li>Go to <strong>Developers → API Keys</strong></li>
                <li>Click <strong>"Create restricted key"</strong></li>
                <li>Give it any name (e.g. "Tavelo")</li>
                <li>Under Permissions, set <strong>Products</strong>, <strong>Prices</strong> and <strong>Payment Links</strong> to <strong>Write</strong></li>
                <li>Click Create, then copy and paste the key here</li>
              </ol>
              <div style={{ marginTop: 8, color: CL.bark }}>Use a test key (<em>rk_test_...</em>) while testing, a live key (<em>rk_live_...</em>) for real payments. Leave blank to disable.</div>
            </>
          )}
        </div>
      </div>
      )}
      <button
        onClick={handleSave}
        style={{
          padding: '8px 20px', background: CL.bark, color: '#fff',
          border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}
      >
        {saved ? tS.saved : tS.save}
      </button>
    </div>
  );
}

function NotificationsSection({ property, lang, tS, saveSection, markDirty }) {
  const [notifyEmail, setNotifyEmail] = useState(property?.notify_email ?? '');
  const [error, setError]             = useState('');
  const [saved, setSaved]             = useState(false);

  const authEmail = ''; // owner email not available client-side; Edge Function handles fallback

  const labels = lang === 'bg' ? {
    inputLabel: 'Имейл за нови запитвания за резервация',
    placeholder: 'info@вашхотел.bg',
    fallbackWith: (e) => `Оставете празно, за да използвате имейла на акаунта (${e}).`,
    fallbackNoAuth: 'Оставете празно, за да използвате имейла на акаунта.',
    invalid: 'Невалиден имейл',
    save: 'Запази',
    saved: '✓ Запазено',
  } : {
    inputLabel: 'Email for new booking requests',
    placeholder: 'info@yourhotel.com',
    fallbackWith: (e) => `Leave blank to use your account email (${e}).`,
    fallbackNoAuth: 'Leave blank to use your account email.',
    invalid: 'Invalid email address',
    save: 'Save',
    saved: '✓ Saved',
  };

  function isValidEmail(v) {
    if (!v) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  }

  async function handleSave() {
    const trimmed = notifyEmail.trim();
    if (!isValidEmail(trimmed)) {
      setError(labels.invalid);
      return;
    }
    setError('');
    await saveSection('notifications', { notify_email: trimmed || null });
    if (!trimmed) setNotifyEmail('');
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ padding: '14px 16px 16px' }}>
      <div style={{ marginBottom: 12 }}>
        <label style={st.lbl}>{labels.inputLabel}</label>
        <input
          type="email"
          value={notifyEmail}
          onChange={e => { setNotifyEmail(e.target.value); setError(''); markDirty('notifications'); }}
          placeholder={labels.placeholder}
          style={{ ...st.inp, ...(error ? { borderColor: CL.terra } : {}) }}
        />
        {error && (
          <div style={{ fontSize: 12, color: CL.terra, marginTop: 6 }}>{error}</div>
        )}
        <div style={{ fontSize: 12, color: CL.stone, marginTop: 8 }}>
          {authEmail ? labels.fallbackWith(authEmail) : labels.fallbackNoAuth}
        </div>
      </div>

      <button
        onClick={handleSave}
        style={{
          padding: '10px 16px',
          background: CL.bark,
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {saved ? labels.saved : labels.save}
      </button>
    </div>
  );
}

function DataBackupSection({ property, tS }) {
  const [status, setStatus] = useState('idle'); // idle | preparing | done | error
  const L = tS.backup;

  async function handleDownload() {
    if (status === 'preparing') return;
    setStatus('preparing');
    try {
      await downloadBackup(property);
      setStatus('done');
    } catch (e) {
      Sentry.captureException(e, {
        tags: { property_id: property?.id },
        extra: { feature: 'data-backup' },
      });
      setStatus('error');
    }
  }

  const statusText =
    status === 'preparing' ? L.preparing :
    status === 'done'      ? L.done :
    status === 'error'     ? L.error : '';

  return (
    <div style={{ padding: '14px 16px 16px' }}>
      <div style={{ fontSize: 13, color: CL.stone, marginBottom: 12, lineHeight: 1.5 }}>
        {L.helper}
      </div>
      <button
        type="button"
        onClick={handleDownload}
        disabled={status === 'preparing'}
        style={{
          padding: '10px 16px',
          background: CL.bark,
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: status === 'preparing' ? 'default' : 'pointer',
          opacity: status === 'preparing' ? 0.6 : 1,
        }}
      >
        {status === 'preparing' ? L.preparing : L.button}
      </button>
      <div
        aria-live="polite"
        role="status"
        style={{ fontSize: 12, color: status === 'error' ? CL.terra : CL.stone, marginTop: 8, minHeight: 16 }}
      >
        {statusText}
      </div>
    </div>
  );
}

export { SettingsPanel };
